/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Clients API
 * API for managing clients (cliënten) within an organization. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface AiRecapResponse {
    /**
     * AI-generated summary of recent developments
     */
    'summary': string;
    /**
     * Timestamp when the summary was generated (milliseconds since epoch)
     */
    'generatedAt': number;
    /**
     * List of data sources that were used to generate the summary
     */
    'dataSourcesUsed': Array<AiRecapResponseDataSourcesUsedEnum>;
}

export const AiRecapResponseDataSourcesUsedEnum = {
    Appointments: 'appointments',
    Forms: 'forms',
    Transcriptions: 'transcriptions'
} as const;

export type AiRecapResponseDataSourcesUsedEnum = typeof AiRecapResponseDataSourcesUsedEnum[keyof typeof AiRecapResponseDataSourcesUsedEnum];

/**
 * Request to assign a form to a client
 */
export interface AssignFormRequest {
    /**
     * ID of the form to assign
     */
    'formId': string;
    /**
     * Optional due date for completion (epoch milliseconds)
     */
    'dueDate'?: number | null;
}
/**
 * What kind of actor performed the action. Defined identically in openapi-audit.yaml. - PERSON: a person — a user, a cliënt or an admin - API_KOPPELING: an API-koppeling acting as its service user; `apiKoppeling` names it 
 */

export const AuditActorKind = {
    Person: 'PERSON',
    ApiKoppeling: 'API_KOPPELING'
} as const;

export type AuditActorKind = typeof AuditActorKind[keyof typeof AuditActorKind];


/**
 * The API-koppeling an access came through. Defined identically in openapi-audit.yaml.
 */
export interface AuditApiKoppeling {
    /**
     * API-koppeling identifier
     */
    'id': string;
    /**
     * The name the organisation gave the koppeling
     */
    'name': string;
}
/**
 * Request body for bulk capabilities lookup
 */
export interface BulkCapabilitiesRequest {
    /**
     * List of client UUIDs to get capabilities for (max 50)
     */
    'clientIds': Array<string>;
}
export interface CareCircleContactResponse {
    /**
     * Contact unique identifier
     */
    'id': string;
    /**
     * Client this contact belongs to
     */
    'clientId': string;
    'role': CareCircleRole;
    /**
     * Contact name
     */
    'name': string;
    /**
     * Contact email address
     */
    'email'?: string | null;
    /**
     * Contact phone number
     */
    'phone'?: string | null;
    /**
     * Relationship description (for personal contacts)
     */
    'relation'?: string | null;
    /**
     * Contact street address
     */
    'addressStreet'?: string | null;
    /**
     * Contact postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * Contact city
     */
    'addressCity'?: string | null;
    /**
     * AGB code of the zorgverlener themselves (8 digits) — ZorgMail\'s `zvAgbCode`. Kept distinct from `praktijkAgbCode`: a ZorgMail mailbox alias exists for the zorgverlener, the praktijk, or both, and matching the one we hold against the other\'s alias reports a reachable huisarts as unreachable. 
     */
    'agbCode'?: string | null;
    /**
     * AGB code of the praktijk this contact works at (8 digits) — ZorgMail\'s `prAgbCode`. See `agbCode`.
     */
    'praktijkAgbCode'?: string | null;
    /**
     * Practice/clinic name (for GP/specialists)
     */
    'praktijkName'?: string | null;
    /**
     * Practice street address
     */
    'praktijkStreet'?: string | null;
    /**
     * Practice postal code
     */
    'praktijkPostalCode'?: string | null;
    /**
     * Practice city
     */
    'praktijkCity'?: string | null;
    /**
     * Additional notes about this contact
     */
    'notes'?: string | null;
    /**
     * The iJW soort relatie (`LDT_SoortRelatie`), when recorded.
     */
    'soortRelatie'?: SoortRelatie | null;
    /**
     * Whether this person holds gezag over the cliënt. Recorded explicitly, never inferred from `role` or `soortRelatie` — parenthood does not imply gezag. Grants nothing on its own. 
     */
    'heeftGezag': boolean;
    /**
     * JW301 `Relatie/Volgorde` — the contact person\'s priority (1 = first contact, 2 = second).
     */
    'volgorde'?: number | null;
    /**
     * First day of the relation; open when absent.
     */
    'startdatum'?: string | null;
    /**
     * Last day of the relation; open when absent. Must not be before `startdatum`.
     */
    'einddatum'?: string | null;
    /**
     * Whether this contact is active
     */
    'isActive': boolean;
    /**
     * Timestamp when contact was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when contact was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}


/**
 * Type of care circle contact: - HUISARTS: General practitioner (GP) - CONTACT_PERSOON: Primary contact person - PARTNER: Partner/spouse - OUDER: Parent - VOOGD: Guardian (voogd). Holds gezag by law, but gezag is still recorded on the contact as `heeftGezag` - KIND: Child - WMO_CONSULENT: WMO consultant - VERWIJZER: Referrer - SPECIALIST: Medical specialist - MANTELZORGER: Informal caregiver - ANDERS: Other 
 */

export const CareCircleRole = {
    Huisarts: 'HUISARTS',
    ContactPersoon: 'CONTACT_PERSOON',
    Partner: 'PARTNER',
    Ouder: 'OUDER',
    Voogd: 'VOOGD',
    Kind: 'KIND',
    WmoConsulent: 'WMO_CONSULENT',
    Verwijzer: 'VERWIJZER',
    Specialist: 'SPECIALIST',
    Mantelzorger: 'MANTELZORGER',
    Anders: 'ANDERS'
} as const;

export type CareCircleRole = typeof CareCircleRole[keyof typeof CareCircleRole];


/**
 * Action performed in a client activity entry. Mirrors the unified audit action enum.  Mutation actions: CREATE, UPDATE, DELETE, ARCHIVE, RESTORE, STATUS_CHANGE, FINALIZE, EXPOSE, UNEXPOSE, ACTIVATE, DEPRECATE, SUBMIT, ACKNOWLEDGE, SEND, PURGE.  Access actions: VIEW, LIST, DOWNLOAD, EXPORT. 
 */

export const ClientActivityAction = {
    Create: 'CREATE',
    Update: 'UPDATE',
    Delete: 'DELETE',
    Archive: 'ARCHIVE',
    Restore: 'RESTORE',
    StatusChange: 'STATUS_CHANGE',
    Finalize: 'FINALIZE',
    Expose: 'EXPOSE',
    Unexpose: 'UNEXPOSE',
    Activate: 'ACTIVATE',
    Deprecate: 'DEPRECATE',
    Submit: 'SUBMIT',
    Acknowledge: 'ACKNOWLEDGE',
    Send: 'SEND',
    Purge: 'PURGE',
    View: 'VIEW',
    List: 'LIST',
    Download: 'DOWNLOAD',
    Export: 'EXPORT'
} as const;

export type ClientActivityAction = typeof ClientActivityAction[keyof typeof ClientActivityAction];


/**
 * A single entry in the client activity feed.
 */
export interface ClientActivityEntry {
    /**
     * Audit log entry identifier
     */
    'id': string;
    /**
     * When the action occurred (ISO 8601, UTC)
     */
    'occurredAt': string;
    /**
     * Display name of the user or client who performed the action. Null when the actor cannot be resolved (e.g., system action). 
     */
    'actorName'?: string | null;
    /**
     * Display name of the admin user when the action was performed during an impersonation session. Null otherwise. 
     */
    'impersonatorName'?: string | null;
    'actorKind': AuditActorKind;
    'apiKoppeling'?: AuditApiKoppeling;
    'action': ClientActivityAction;
    /**
     * Type of resource the activity relates to (e.g., Client, Appointment, Document). Used as the deep-link target type. 
     */
    'resourceType': string;
    /**
     * Identifier of the resource the activity relates to. Used as the deep-link target id. 
     */
    'resourceId': string;
    /**
     * Developer-facing one-line summary (`<action> <resourceType>: <fields>`), not written for display. Read `action`, `resourceType` and `changedFields` instead; kept until no deployed frontend reads it. 
     * @deprecated
     */
    'summary': string;
    /**
     * Names of the fields a mutation changed, in the order recorded. Empty for an action that changed no field (a create, a read). 
     */
    'changedFields': Array<string>;
    /**
     * True when the actor reached for this resource and was **refused** — nothing about this cliënt was disclosed. `action` is then `VIEW`, so an entry that is a refused attempt is only distinguishable from a served read by this field.  Refusals carry `VIEW`, so they appear in this feed only when `includeViews` is set, which requires the `audit.read` permission. See ADR-024 §3e. 
     */
    'denied': boolean;
    /**
     * Client IP address. Only populated when the caller has the `audit.read` permission; otherwise omitted. 
     */
    'ipAddress'?: string | null;
    /**
     * Client User-Agent string. Only populated when the caller has the `audit.read` permission; otherwise omitted. 
     */
    'userAgent'?: string | null;
}


/**
 * Paginated client activity feed.
 */
export interface ClientActivityPage {
    /**
     * Activity entries ordered by occurredAt DESC.
     */
    'content': Array<ClientActivityEntry>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of entries matching the filters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface ClientAvatarResponse {
    /**
     * URL to the uploaded avatar image
     */
    'avatarUrl': string;
}
export interface ClientBehandelaarResponse {
    /**
     * Assignment unique identifier
     */
    'id': string;
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * User (behandelaar) UUID. A Scrivio user id, the one other public operations name a behandelaar by.
     */
    'userId': string;
    /**
     * Full name of behandelaar
     */
    'userName': string;
    /**
     * Timestamp when assignment was created (milliseconds since epoch)
     */
    'assignedAt': number;
}
/**
 * Authorization capabilities for a specific client. Each property indicates whether the authenticated user can perform the corresponding action on this client. 
 */
export interface ClientCapabilities {
    /**
     * Can view the client dossier
     */
    'view': boolean;
    /**
     * Can update client information
     */
    'update': boolean;
    /**
     * Can archive the client (soft-delete)
     */
    'archive': boolean;
    /**
     * User is allowed to archive clients, but this client cannot be archived right now because it still has an open zorgtraject. Lets the UI show a disabled archive action with an explanation rather than hiding it. 
     */
    'archiveBlockedByZorgtraject': boolean;
    /**
     * Can restore the client from the archive
     */
    'unarchive': boolean;
    /**
     * Can assign a behandelaar to the client
     */
    'assignBehandelaar': boolean;
    /**
     * Can remove a behandelaar from the client
     */
    'unassignBehandelaar': boolean;
    /**
     * Can create a new zorgtraject for the client
     */
    'createZorgtraject': boolean;
    /**
     * Can create appointments for the client
     */
    'createAppointment': boolean;
    /**
     * Can send the client an invitation mail to the cliëntportaal (#4718). Says nothing about whether the dossier has an e-mailadres; the endpoint answers that with a 422. 
     */
    'inviteToPortal': boolean;
}
export interface ClientCommentResponse {
    /**
     * Comment unique identifier
     */
    'id': string;
    /**
     * Client this comment belongs to
     */
    'clientId': string;
    /**
     * User ID of the comment author
     */
    'authorId': string;
    /**
     * Full name of the comment author
     */
    'authorName': string;
    /**
     * Comment content
     */
    'content': string;
    /**
     * Timestamp when comment was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when comment was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}
export interface ClientFinancieringResponse {
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * Active funding sources
     */
    'fundingSources': Array<FundingSource>;
    /**
     * Insurance policies linked to this financiering record. Empty for clients without any policies yet. 
     */
    'policies': Array<ClientInsurancePolicy>;
    /**
     * IBAN for payments
     */
    'iban'?: string | null;
    /**
     * Account holder name
     */
    'rekeninghouder'?: string | null;
    /**
     * Remarks for Eigen Bijdrage section
     */
    'eigenBijdrageRemarks'?: string | null;
    /**
     * Timestamp when record was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when record was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}
/**
 * One financing (financiering) a client holds under a single financing regime, with the regime-specific authorization detail: the indicatie/beschikking window, the allocated omvang, and the WMO consulent contact (ADR-030). 
 */
export interface ClientFinancingResponse {
    /**
     * Financing UUID
     */
    'id': string;
    /**
     * Client this financing belongs to
     */
    'clientId': string;
    'financingSource': FinancingRegime;
    /**
     * Specific law/regulation reference (vrije tekst)
     */
    'wet'?: string | null;
    /**
     * \"Financiering rond: Ja/Nee\" — whether funding is fully arranged
     */
    'financieringRond'?: boolean | null;
    'bekostigingsgroep'?: string | null;
    'indicatieNummer'?: string | null;
    'indicatieStart'?: string | null;
    'indicatieEnd'?: string | null;
    /**
     * Tariff contract reference
     */
    'tariefContract'?: string | null;
    'minutenPerWeek'?: number | null;
    'urenPerWeek'?: number | null;
    'uurtarief'?: number | null;
    'wmoConsulentName'?: string | null;
    'wmoConsulentGemeente'?: string | null;
    'wmoConsulentEmail'?: string | null;
    'wmoConsulentPhone'?: string | null;
    'label'?: string | null;
    'opmerking'?: string | null;
    /**
     * Whether this financing is currently in force
     */
    'isActive': boolean;
    /**
     * Timestamp when the financing was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when the financing was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}


/**
 * List of client form assignments
 */
export interface ClientFormAssignmentListResponse {
    'assignments': Array<ClientFormAssignmentResponse>;
    /**
     * Total number of assignments
     */
    'totalCount': number;
}
/**
 * A form assignment record with form summary
 */
export interface ClientFormAssignmentResponse {
    /**
     * Assignment UUID
     */
    'id': string;
    /**
     * Form UUID
     */
    'formId': string;
    /**
     * Title of the assigned form
     */
    'formTitle': string;
    /**
     * Description of the assigned form
     */
    'formDescription'?: string | null;
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * User UUID who assigned the form
     */
    'assignedBy': string;
    /**
     * Assignment timestamp (epoch milliseconds)
     */
    'assignedAt': number;
    /**
     * Due date for completion (epoch milliseconds)
     */
    'dueDate'?: number | null;
    /**
     * Completion timestamp (epoch milliseconds)
     */
    'completedAt'?: number | null;
    'status': FormAssignmentStatus;
    /**
     * Whether the assignment is visible in the client portal. Assigning a form does not share it: portal visibility is opt-in and defaults to false. 
     */
    'sharedWithClient': boolean;
    /**
     * Timestamp the assignment was shared with the client (epoch milliseconds)
     */
    'sharedAt'?: number | null;
    /**
     * Present only when the request asked to notify the client (`notifyClient: true`). True when the mail was handed to delivery after the change committed; false when it was skipped — the dossier has no e-mailadres, or the request did not share anything new. The change itself succeeded either way. 
     */
    'clientNotified'?: boolean;
    /**
     * Number of questions in the form
     */
    'questionCount'?: number;
}


export interface ClientInsurancePolicy {
    /**
     * Policy UUID
     */
    'id': string;
    /**
     * Reference to the parent client_financiering row
     */
    'clientFinancieringId': string;
    /**
     * Reference to insurer (from insurers table)
     */
    'insurerId': string;
    /**
     * Resolved insurer name (read-only, for display purposes)
     */
    'insurerName'?: string | null;
    /**
     * Insured person identifier (sensitive — redacted in audit log)
     */
    'verzekerdenummer'?: string | null;
    /**
     * Insurance type (e.g. `Basis`, `Aanvullend`)
     */
    'soortVerzekering'?: string | null;
    /**
     * Insurance package code
     */
    'pakketCode'?: string | null;
    /**
     * Insurance package name
     */
    'pakketNaam'?: string | null;
    /**
     * Insurance label code
     */
    'labelCode'?: string | null;
    /**
     * Insurance label name
     */
    'labelNaam'?: string | null;
    /**
     * Validity start date
     */
    'ingangsdatum'?: string | null;
    /**
     * Validity end date
     */
    'einddatum'?: string | null;
    /**
     * Whether the policy is currently flagged active
     */
    'isActive': boolean;
    'source': InsurancePolicySource;
    /**
     * Timestamp of last COV verification (milliseconds since epoch)
     */
    'lastVerifiedAt'?: number | null;
    /**
     * Timestamp when policy was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when policy was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}


/**
 * The language a client reads — what is sent to them and the portal they sign in to. NL unless the practice chose otherwise. Defined identically in openapi-portal.yaml, which returns it on the portal\'s own profile.
 */

export const ClientLanguage = {
    Nl: 'NL',
    En: 'EN'
} as const;

export type ClientLanguage = typeof ClientLanguage[keyof typeof ClientLanguage];


export interface ClientListResponse {
    /**
     * List of clients for current page
     */
    'content': Array<ClientProfile>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of clients matching criteria
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * Total number of non-archived clients regardless of filters (used for \'All\' count)
     */
    'totalUnfilteredCount': number;
    /**
     * Per-state counts of non-archived clients visible to the caller, regardless of the current status/location/search filters. Powers the status tabs and count summary. Clients without a current state are not represented.
     */
    'statusCounts'?: Array<ClientStatusCount>;
}
export interface ClientProfile {
    /**
     * Client unique identifier
     */
    'id': string;
    /**
     * Organization this client belongs to
     */
    'organizationId': string;
    /**
     * Client first name
     */
    'firstName': string;
    /**
     * Client last name
     */
    'lastName': string;
    /**
     * Name prefix (tussenvoegsel)
     */
    'prefix'?: string | null;
    /**
     * Client initials
     */
    'initials'?: string | null;
    /**
     * Client email address
     */
    'email'?: string | null;
    /**
     * Client phone number
     */
    'phone'?: string | null;
    /**
     * Street name only. The house number is `addressHouseNumber`; a caller that holds one composed line must split it before sending, because nothing here splits it for them.
     */
    'addressStreet'?: string | null;
    /**
     * House number, without its toevoeging
     */
    'addressHouseNumber'?: string | null;
    /**
     * Huisletter and huisnummertoevoeging together, as the zib carries them
     */
    'addressHouseNumberSuffix'?: string | null;
    /**
     * Postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * City
     */
    'addressCity'?: string | null;
    /**
     * Date of birth (YYYY-MM-DD)
     */
    'dateOfBirth'?: string | null;
    /**
     * Burgerservicenummer (Dutch citizen service number, 9 digits).  A value written today also passes the elfproef (11-check), which OpenAPI cannot express. A value stored before that rule was introduced may not. 
     */
    'bsn'?: string | null;
    'gender'?: Gender | null;
    /**
     * Location ID where this client receives services. No operation of the public API lists locations yet.
     */
    'locationId'?: string | null;
    /**
     * Per-organization patient number. Auto-generated (P-{seq}) on native create, or preserved verbatim when supplied on import. Unique per organization and immutable after creation.
     */
    'patientNumber'?: string | null;
    /**
     * The client\'s number in the source system this client was imported from (for a ZPM import, the export\'s client_id / ZorgAdmin id). Set by the import and searchable, so an organization can find a client by the number they use themselves. Not unique - source systems can collide.
     */
    'externalReference'?: string | null;
    /**
     * Name of the location (computed from location record)
     */
    'locationName'?: string | null;
    /**
     * Name of the currently active zorgtraject for this client: the behandelsetting of its current setting period. Null when the client has no active zorgtraject, when that zorgtraject has no setting period, and always when `activeZorgtrajectFinancingRegime` is set and is not `ZVW` — such a zorgtraject has no behandelsetting and is named by its regime instead.  Computed for display in the Scrivio app. 
     */
    'activeZorgtrajectName'?: string | null;
    /**
     * Financing regime of the currently active zorgtraject. Null when the client has no active zorgtraject, or when its regime was never recorded — which is named like `ZVW`. 
     */
    'activeZorgtrajectFinancingRegime'?: FinancingRegime | null;
    /**
     * Date and time of the next scheduled appointment (ISO 8601). Computed for display in the Scrivio app.
     */
    'nextAppointmentDate'?: string | null;
    /**
     * Current client state name, computed from ClientStatePeriod history. Use the client-states API to manage state changes.
     */
    'status'?: string | null;
    /**
     * URL to client avatar image. A path in the Scrivio app; the avatar operation is not part of the public API.
     */
    'avatarUrl'?: string | null;
    /**
     * Date of intake (YYYY-MM-DD)
     */
    'intakeDate'?: string | null;
    /**
     * Date of discharge (YYYY-MM-DD)
     */
    'dischargeDate'?: string | null;
    /**
     * Timestamp when client was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when client was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
    /**
     * Timestamp when client was archived (milliseconds since epoch, null if not archived)
     */
    'archivedAt'?: number | null;
    /**
     * When the cliënt was last sent a cliëntportaal invitation (milliseconds since epoch, null if never). A resend overwrites it. 
     */
    'portalInvitedAt'?: number | null;
    'idDocumentType'?: IdDocumentType | null;
    /**
     * Number of the identity document used to establish the client\'s identity (Wabvpz). Registered but never accompanied by a stored scan.
     */
    'idDocumentNumber'?: string | null;
    /**
     * Timestamp when the client\'s identity was verified (milliseconds since epoch, null if never verified)
     */
    'idVerifiedAt'?: number | null;
    /**
     * User who verified the client\'s identity (null if never verified). A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns.
     */
    'idVerifiedBy'?: string | null;
    /**
     * Level of the client\'s one standing warning, null exactly when there is none. Always null together with `warningText`: the two are one fact, and a database CHECK refuses any other pairing. 
     */
    'warningLevel'?: ClientWarningLevel | null;
    /**
     * The client\'s one standing warning — free text a practitioner typed about this client, surfaced wherever the client is opened (their profile, and every appointment they take part in). Null exactly when there is no warning. 
     */
    'warningText'?: string | null;
    /**
     * Derived, never stored: true when any of the client\'s active care-circle contacts holds gezag (`heeftGezag`) today — inside its `startdatum`/`einddatum` period, open ends unbounded. JW315\'s optional `GezagsdragerBekend`. Grants nothing on its own. 
     */
    'gezagsdragerBekend'?: boolean;
    'language': ClientLanguage;
}


export interface ClientStatusCount {
    /**
     * The client state (status) these clients are currently in
     */
    'clientStateId': string;
    /**
     * Number of non-archived clients currently in this state
     */
    'count': number;
}
/**
 * Paginated client dossier timeline.
 */
export interface ClientTimelinePage {
    /**
     * Timeline events ordered by occurredAt DESC. May contain fewer than `size` items when candidate audit rows on this page carry no semantic mapping. 
     */
    'content': Array<TimelineEvent>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of candidate audit rows for this client. An upper bound on the number of events: rows dropped by the semantic mapper are still counted. 
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * How loudly a client\'s standing warning is drawn. INFO is something to know, WARNING something to watch, CRITICAL something that changes what you do before you open the door. Defined identically in openapi-appointments.yaml, which needs it on AppointmentParticipant.
 */

export const ClientWarningLevel = {
    Info: 'INFO',
    Warning: 'WARNING',
    Critical: 'CRITICAL'
} as const;

export type ClientWarningLevel = typeof ClientWarningLevel[keyof typeof ClientWarningLevel];


export interface ClientZorgvraagResponse {
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * Initial complaint or reason for referral
     */
    'aanmeldklacht'?: string | null;
    /**
     * Client\'s help request
     */
    'hulpvraag'?: string | null;
    /**
     * Goals and objectives
     */
    'doelen'?: Array<string>;
    /**
     * Type of help/support being offered
     */
    'hulpaanbod'?: string | null;
    /**
     * Timestamp when record was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when record was last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}
export interface CovSkippedCoverage {
    /**
     * UZOVI code from the COV response that has no insurer in the catalogue
     */
    'uzoviCode': string;
    /**
     * Insurer label as VECOZO reported it, so the user can recognise the missing coverage. Null when COV returned no label. 
     */
    'insurerName'?: string | null;
}
export interface CovSyncResponse {
    'financiering': ClientFinancieringResponse;
    /**
     * Coverages VECOZO returned that could not be stored because their UZOVI code is unknown to the insurer catalogue. Empty on a fully successful sync; when non-empty the client\'s coverage is incompletely registered and the user must be told. 
     */
    'skippedCoverages': Array<CovSkippedCoverage>;
}
/**
 * Stable, machine-readable reason a VECOZO COV verification could not be completed.  The client maps each code onto a localized message that names a concrete next step. The problem\'s `detail` is a generic fallback only: the reason must never reach the user as the raw transport/provider text (#3280).  `ACCEPTANCE_TEST_BSN_REQUIRED` is a refusal by Scrivio rather than an answer from VECOZO: this deployment talks to a non-production VECOZO environment, which accepts only VECOZO\'s whitelisted fictitious persons, and this client\'s BSN is not one of them. Nothing was sent (ADR-031 Decision 4, ADR-044).  `VECOZO_AGREEMENT_MISSING` is a refusal by Scrivio too, and the one an organization clears itself: the practice has not confirmed it arranged its VECOZO aansluiting and the toestemmingsverklaring for its AGB-code, so nothing was sent. It is distinct from `NOT_AUTHORIZED_FOR_INSURER` with no UZOVI, which is VECOZO answering that the toestemmingsverklaring really is absent — the remedy is the same but the second costs a round trip and arrives as a fault nobody can act on (#3016). 
 */

export const CovVerificationErrorCode = {
    BsnMissing: 'BSN_MISSING',
    BsnInvalid: 'BSN_INVALID',
    GeboortedatumMissing: 'GEBOORTEDATUM_MISSING',
    BsnGeboortedatumMismatch: 'BSN_GEBOORTEDATUM_MISMATCH',
    BsnUnknown: 'BSN_UNKNOWN',
    InsuranceNotFound: 'INSURANCE_NOT_FOUND',
    MultiplePersonsFound: 'MULTIPLE_PERSONS_FOUND',
    NotAuthorizedForInsurer: 'NOT_AUTHORIZED_FOR_INSURER',
    InsurerNotInDirectory: 'INSURER_NOT_IN_DIRECTORY',
    AgbCodeMissing: 'AGB_CODE_MISSING',
    AcceptanceTestBsnRequired: 'ACCEPTANCE_TEST_BSN_REQUIRED',
    VecozoAgreementMissing: 'VECOZO_AGREEMENT_MISSING',
    VecozoUnavailable: 'VECOZO_UNAVAILABLE'
} as const;

export type CovVerificationErrorCode = typeof CovVerificationErrorCode[keyof typeof CovVerificationErrorCode];


/**
 * RFC 7807 Problem Details for a failed VECOZO COV verification, extended with the machine-readable `errorCode` and the values the message needs. 
 */
export interface CovVerificationProblem {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
    'errorCode': CovVerificationErrorCode;
    /**
     * UZOVI codes the error is about. Set for `NOT_AUTHORIZED_FOR_INSURER` (the insurer the organization may not query) and `INSURER_NOT_IN_DIRECTORY` (the insurers VECOZO returned that Scrivio does not know).  `NOT_AUTHORIZED_FOR_INSURER` also arrives **empty**: VECOZO refuses a call whose `Zorgaanbieder` AGB-code has no toestemmingsverklaring with a message-level fault, which is about the organization rather than about one insurer. The remedy is the same, so the code is the same. 
     */
    'uzoviCodes'?: Array<string>;
    /**
     * Reference date the verification was performed for. Absent for the pre-flight failures (`BSN_MISSING`, `BSN_INVALID`, `GEBOORTEDATUM_MISSING`, `AGB_CODE_MISSING`), which never reached VECOZO.  `ACCEPTANCE_TEST_BSN_REQUIRED` is the exception among those: nothing was sent there either, but the refusal is about this client\'s BSN *for that date*, so it carries the date the refused verification was for. 
     */
    'peildatum'?: string;
}


export interface CreateCareCircleContactRequest {
    'role': CareCircleRole;
    /**
     * Contact name
     */
    'name': string;
    /**
     * Contact email address
     */
    'email'?: string | null;
    /**
     * Contact phone number
     */
    'phone'?: string | null;
    /**
     * Relationship description (for personal contacts)
     */
    'relation'?: string | null;
    /**
     * Contact street address
     */
    'addressStreet'?: string | null;
    /**
     * Contact postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * Contact city
     */
    'addressCity'?: string | null;
    /**
     * AGB code of the zorgverlener themselves (8 digits) — ZorgMail\'s `zvAgbCode`. Kept distinct from `praktijkAgbCode`: a ZorgMail mailbox alias exists for the zorgverlener, the praktijk, or both, and matching the one we hold against the other\'s alias reports a reachable huisarts as unreachable. 
     */
    'agbCode'?: string | null;
    /**
     * AGB code of the praktijk this contact works at (8 digits) — ZorgMail\'s `prAgbCode`. See `agbCode`.
     */
    'praktijkAgbCode'?: string | null;
    /**
     * Practice/clinic name (for GP/specialists)
     */
    'praktijkName'?: string | null;
    /**
     * Practice street address
     */
    'praktijkStreet'?: string | null;
    /**
     * Practice postal code
     */
    'praktijkPostalCode'?: string | null;
    /**
     * Practice city
     */
    'praktijkCity'?: string | null;
    /**
     * Additional notes about this contact
     */
    'notes'?: string | null;
    /**
     * The iJW soort relatie (`LDT_SoortRelatie`), when recorded.
     */
    'soortRelatie'?: SoortRelatie | null;
    /**
     * Whether this person holds gezag over the cliënt. Recorded explicitly, never inferred from `role` or `soortRelatie` — parenthood does not imply gezag. Grants nothing on its own. Omitted on create means false. 
     */
    'heeftGezag'?: boolean;
    /**
     * JW301 `Relatie/Volgorde` — the contact person\'s priority (1 = first contact, 2 = second).
     */
    'volgorde'?: number | null;
    /**
     * First day of the relation; open when absent.
     */
    'startdatum'?: string | null;
    /**
     * Last day of the relation; open when absent. Must not be before `startdatum`.
     */
    'einddatum'?: string | null;
}


export interface CreateClientCommentRequest {
    /**
     * Comment content
     */
    'content': string;
}
/**
 * A newly recorded financing is active; use the update operation to deactivate one without losing its history. 
 */
export interface CreateClientFinancingRequest {
    'financingSource': FinancingRegime;
    'wet'?: string | null;
    'financieringRond'?: boolean | null;
    'bekostigingsgroep'?: string | null;
    'indicatieNummer'?: string | null;
    'indicatieStart'?: string | null;
    'indicatieEnd'?: string | null;
    'tariefContract'?: string | null;
    'minutenPerWeek'?: number | null;
    'urenPerWeek'?: number | null;
    'uurtarief'?: number | null;
    'wmoConsulentName'?: string | null;
    'wmoConsulentGemeente'?: string | null;
    'wmoConsulentEmail'?: string | null;
    'wmoConsulentPhone'?: string | null;
    'label'?: string | null;
    'opmerking'?: string | null;
}


export interface CreateClientInsurancePolicyRequest {
    /**
     * Reference to insurer (from insurers table)
     */
    'insurerId': string;
    'verzekerdenummer'?: string | null;
    'soortVerzekering'?: string | null;
    'pakketCode'?: string | null;
    'pakketNaam'?: string | null;
    'labelCode'?: string | null;
    'labelNaam'?: string | null;
    'ingangsdatum'?: string | null;
    'einddatum'?: string | null;
    'isActive'?: boolean;
    'source'?: InsurancePolicySource;
}


export interface CreateClientRequest {
    /**
     * Client first name
     */
    'firstName': string;
    /**
     * Client last name
     */
    'lastName': string;
    /**
     * Name prefix (tussenvoegsel)
     */
    'prefix'?: string | null;
    /**
     * Client initials
     */
    'initials'?: string | null;
    /**
     * Client email address
     */
    'email'?: string | null;
    /**
     * Client phone number
     */
    'phone'?: string | null;
    /**
     * Street name only. The house number is `addressHouseNumber`; a caller that holds one composed line must split it before sending, because nothing here splits it for them.
     */
    'addressStreet'?: string | null;
    /**
     * House number, without its toevoeging
     */
    'addressHouseNumber'?: string | null;
    /**
     * Huisletter and huisnummertoevoeging together, as the zib carries them
     */
    'addressHouseNumberSuffix'?: string | null;
    /**
     * Postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * City
     */
    'addressCity'?: string | null;
    /**
     * Date of birth (YYYY-MM-DD)
     */
    'dateOfBirth'?: string | null;
    /**
     * Burgerservicenummer (9 digits).  It must also pass the elfproef (11-check), which OpenAPI cannot express: the weighted digit sum 9·d1 + 8·d2 + … + 2·d8 − d9 is positive and divisible by 11. A value that does not is refused with `400` and an `errors` entry naming `bsn`. 
     */
    'bsn'?: string | null;
    'gender'?: Gender | null;
    /**
     * Location ID where this client receives services. No operation of the public API lists locations yet.
     */
    'locationId'?: string | null;
    /**
     * Optional patient number carried over from an external system (e.g. Zorgadmin). Preserved verbatim and must be unique within the organization; a duplicate is rejected with 409. When omitted, Scrivio auto-generates one (P-{seq}).
     */
    'patientNumber'?: string | null;
    /**
     * Date of intake (YYYY-MM-DD)
     */
    'intakeDate'?: string | null;
    'idDocumentType'?: IdDocumentType | null;
    /**
     * Identity document number (Wabvpz). When both idDocumentType and idDocumentNumber are provided, the client\'s identity is marked as verified (idVerifiedAt/idVerifiedBy are stamped). No scan is stored.
     */
    'idDocumentNumber'?: string | null;
    /**
     * Level of the client\'s one standing warning. Ignored when `warningText` is blank or omitted — a warning *is* its text, so a level with nothing under it stores nothing. A text sent without a level is stored as WARNING; OpenAPI 3.0 cannot express either rule, which is why they are stated here. 
     */
    'warningLevel'?: ClientWarningLevel | null;
    /**
     * The client\'s one standing warning. Blank or omitted means the client has none. 
     */
    'warningText'?: string | null;
    /**
     * The language the client reads. Omitted means NL.
     */
    'language'?: ClientLanguage;
}


export interface CreateVerantwoordelijkeGemeenteRequest {
    'regime': VerantwoordelijkeGemeenteRegime;
    /**
     * CBS gemeentecode; must name a gemeente on `geldigVanaf`
     */
    'gemeentecode': string;
    'grondslag': VerantwoordelijkeGemeenteGrondslag;
    'geldigVanaf': string;
    /**
     * The received JW301 this confirmation follows, which makes `bron` TOEWIJZING
     */
    'ijwBerichtId'?: string | null;
}


/**
 * Financing regime (wettelijk kader) under which care is funded and declared
 */

export const FinancingRegime = {
    Zvw: 'ZVW',
    Wmo: 'WMO',
    Jeugdwet: 'JEUGDWET',
    Particulier: 'PARTICULIER',
    Pgb: 'PGB',
    Anders: 'ANDERS'
} as const;

export type FinancingRegime = typeof FinancingRegime[keyof typeof FinancingRegime];


/**
 * Status of a form assignment: - PENDING: Form has not been completed yet - COMPLETED: Client has submitted the form 
 */

export const FormAssignmentStatus = {
    Pending: 'PENDING',
    Completed: 'COMPLETED'
} as const;

export type FormAssignmentStatus = typeof FormAssignmentStatus[keyof typeof FormAssignmentStatus];


/**
 * Type of funding source: - ZPM_VERZEKERING: Health insurance (zorgverzekering) - EIGEN_BIJDRAGE: Client\'s own contribution 
 */

export const FundingSource = {
    ZpmVerzekering: 'ZPM_VERZEKERING',
    EigenBijdrage: 'EIGEN_BIJDRAGE'
} as const;

export type FundingSource = typeof FundingSource[keyof typeof FundingSource];


/**
 * Client gender
 */

export const Gender = {
    Male: 'MALE',
    Female: 'FEMALE',
    Other: 'OTHER',
    Unknown: 'UNKNOWN'
} as const;

export type Gender = typeof Gender[keyof typeof Gender];


/**
 * A huisarts as correspondence would address them. Projected from a care circle contact.
 */
export interface HuisartsRecipient {
    /**
     * The care circle contact this is a projection of — the thing to edit to correct it
     */
    'careCircleContactId': string;
    /**
     * Huisarts name
     */
    'name': string;
    /**
     * AGB code of the zorgverlener (ZorgMail `zvAgbCode`)
     */
    'agbCode'?: string | null;
    /**
     * AGB code of the praktijk (ZorgMail `prAgbCode`)
     */
    'praktijkAgbCode'?: string | null;
    /**
     * Practice name
     */
    'praktijkName'?: string | null;
    /**
     * Practice address as one line, or null when no component is recorded
     */
    'praktijkAddress'?: string | null;
    /**
     * Huisarts email address
     */
    'email'?: string | null;
    /**
     * Huisarts phone number
     */
    'phone'?: string | null;
    /**
     * True while this contact is still the verwijzer syncer\'s own (#2496) and no person has adopted it. It is not a weaker recipient — it may be exactly right — but it is a statement about who referred a traject, not about who the cliënt\'s huisarts is now. 
     */
    'derivedFromVerwijzer': boolean;
}
export interface HuisartsRecipientResolution {
    'status': HuisartsRecipientStatus;
    'recipient'?: HuisartsRecipient;
    /**
     * Every active huisarts contact when the cliënt has more than one, so the sender can choose. Empty in every other case — a resolved recipient is not repeated here. 
     */
    'alternatives': Array<HuisartsRecipient>;
    'seedCandidate'?: HuisartsSeedCandidate;
}


/**
 * How confidently the cliënt\'s huisarts can be addressed: - NONE: no active huisarts is recorded on the cliënt - RECORDED: exactly one, put there or adopted by a person - DERIVED_FROM_VERWIJZER: exactly one, still the verwijzer syncer\'s (#2496) — nobody has   confirmed that this is the cliënt\'s huisarts *now*, so a sender is asked before it is used - AMBIGUOUS: more than one active huisarts; only a behandelaar can say which 
 */

export const HuisartsRecipientStatus = {
    None: 'NONE',
    Recorded: 'RECORDED',
    DerivedFromVerwijzer: 'DERIVED_FROM_VERWIJZER',
    Ambiguous: 'AMBIGUOUS'
} as const;

export type HuisartsRecipientStatus = typeof HuisartsRecipientStatus[keyof typeof HuisartsRecipientStatus];


/**
 * The verwijzer of the cliënt\'s active zorgtraject, offered as something a person may copy onto the cliënt. Present only when that verwijzer is a huisarts and no person-recorded huisarts stands in the way. Purely a suggestion — nothing addresses a letter from it. 
 */
export interface HuisartsSeedCandidate {
    /**
     * The zorgtraject the verwijzer was read from
     */
    'careTrajectoryId': string;
    /**
     * Verwijzer name
     */
    'name': string;
    /**
     * The trajectory\'s `agbVerwijzer`, normalised to eight digits where it can be
     */
    'agbCode'?: string | null;
    /**
     * Verwijzer email address
     */
    'email'?: string | null;
    /**
     * Referral date — how stale the suggestion is
     */
    'verwijsdatum'?: string | null;
}
/**
 * Type of identity document used to establish the client\'s identity (Wabvpz). Only the document type and number are registered; a scan or copy of the document is never stored.
 */

export const IdDocumentType = {
    Passport: 'PASSPORT',
    IdCard: 'ID_CARD',
    DriversLicense: 'DRIVERS_LICENSE',
    ResidencePermit: 'RESIDENCE_PERMIT'
} as const;

export type IdDocumentType = typeof IdDocumentType[keyof typeof IdDocumentType];


/**
 * Origin of an insurance policy row: - MANUAL: entered by a user - COV: returned by VECOZO COV insurance verification 
 */

export const InsurancePolicySource = {
    Manual: 'MANUAL',
    Cov: 'COV'
} as const;

export type InsurancePolicySource = typeof InsurancePolicySource[keyof typeof InsurancePolicySource];


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * What the contact is to the cliënt, in iJW\'s terms (`LDT_SoortRelatie`, JW301 `Relatie/Soort`). Only the codes that occur around a minor; the Vektis code is in brackets: - HULPVERLENER: Hulpverlener (07) - ANDERS: Anders (09) - OUDER: Ouder (10) - VOOGD: Voogd (11) - PLEEGOUDER: Pleegouder (13) - FAMILIELID: Familielid (17) - GEZINSLID: Gezinslid (18) — also a parent\'s partner, for whom iJW has no code  A soort says nothing about gezag: that is `heeftGezag`, recorded per relation. 
 */

export const SoortRelatie = {
    Hulpverlener: 'HULPVERLENER',
    Anders: 'ANDERS',
    Ouder: 'OUDER',
    Voogd: 'VOOGD',
    Pleegouder: 'PLEEGOUDER',
    Familielid: 'FAMILIELID',
    Gezinslid: 'GEZINSLID'
} as const;

export type SoortRelatie = typeof SoortRelatie[keyof typeof SoortRelatie];


/**
 * A single semantically typed event in the client dossier timeline.
 */
export interface TimelineEvent {
    /**
     * Audit log entry identifier
     */
    'id': string;
    /**
     * When the event occurred (ISO 8601, UTC)
     */
    'occurredAt': string;
    'type': TimelineEventType;
    /**
     * Dutch one-line label for the event.
     */
    'title': string;
    /**
     * Dutch one-line elaboration of the event.
     */
    'description': string;
    /**
     * Display name of the user or client who performed the action. Null when the actor cannot be resolved (e.g., system action). 
     */
    'actorName'?: string | null;
}


/**
 * Semantic type of a dossier timeline event. Drives the icon and colour of the timeline node; the accompanying `title`/`description` carry the rendered Dutch wording. 
 */

export const TimelineEventType = {
    AppointmentCompleted: 'APPOINTMENT_COMPLETED',
    FormSubmitted: 'FORM_SUBMITTED',
    NoteAdded: 'NOTE_ADDED',
    DocumentAdded: 'DOCUMENT_ADDED',
    CareTrajectoryStarted: 'CARE_TRAJECTORY_STARTED',
    CareTrajectoryClosed: 'CARE_TRAJECTORY_CLOSED'
} as const;

export type TimelineEventType = typeof TimelineEventType[keyof typeof TimelineEventType];


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateCareCircleContactRequest {
    'role'?: CareCircleRole;
    /**
     * Contact name
     */
    'name'?: string | null;
    /**
     * Contact email address
     */
    'email'?: string | null;
    /**
     * Contact phone number
     */
    'phone'?: string | null;
    /**
     * Relationship description (for personal contacts)
     */
    'relation'?: string | null;
    /**
     * Contact street address
     */
    'addressStreet'?: string | null;
    /**
     * Contact postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * Contact city
     */
    'addressCity'?: string | null;
    /**
     * AGB code of the zorgverlener themselves (8 digits) — ZorgMail\'s `zvAgbCode`. Kept distinct from `praktijkAgbCode`: a ZorgMail mailbox alias exists for the zorgverlener, the praktijk, or both, and matching the one we hold against the other\'s alias reports a reachable huisarts as unreachable. 
     */
    'agbCode'?: string | null;
    /**
     * AGB code of the praktijk this contact works at (8 digits) — ZorgMail\'s `prAgbCode`. See `agbCode`.
     */
    'praktijkAgbCode'?: string | null;
    /**
     * Practice/clinic name (for GP/specialists)
     */
    'praktijkName'?: string | null;
    /**
     * Practice street address
     */
    'praktijkStreet'?: string | null;
    /**
     * Practice postal code
     */
    'praktijkPostalCode'?: string | null;
    /**
     * Practice city
     */
    'praktijkCity'?: string | null;
    /**
     * Additional notes about this contact
     */
    'notes'?: string | null;
    /**
     * The iJW soort relatie (`LDT_SoortRelatie`), when recorded.
     */
    'soortRelatie'?: SoortRelatie | null;
    /**
     * Whether this person holds gezag over the cliënt. Recorded explicitly, never inferred from `role` or `soortRelatie` — parenthood does not imply gezag. Grants nothing on its own. 
     */
    'heeftGezag'?: boolean | null;
    /**
     * JW301 `Relatie/Volgorde` — the contact person\'s priority (1 = first contact, 2 = second).
     */
    'volgorde'?: number | null;
    /**
     * First day of the relation; open when absent.
     */
    'startdatum'?: string | null;
    /**
     * Last day of the relation; open when absent. Must not be before `startdatum`.
     */
    'einddatum'?: string | null;
    /**
     * Whether this contact is active
     */
    'isActive'?: boolean | null;
}


export interface UpdateClientCommentRequest {
    /**
     * Updated comment content
     */
    'content'?: string | null;
}
/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateClientFinancieringRequest {
    /**
     * Active funding sources
     */
    'fundingSources'?: Array<FundingSource>;
    /**
     * IBAN for payments
     */
    'iban'?: string | null;
    /**
     * Account holder name
     */
    'rekeninghouder'?: string | null;
    /**
     * Remarks for Eigen Bijdrage section
     */
    'eigenBijdrageRemarks'?: string | null;
}
/**
 * All properties are optional. Only properties present in the request body are updated; omitted ones remain unchanged and an explicit `null` clears the value. 
 */
export interface UpdateClientFinancingRequest {
    'financingSource'?: FinancingRegime;
    'wet'?: string | null;
    'financieringRond'?: boolean | null;
    'bekostigingsgroep'?: string | null;
    'indicatieNummer'?: string | null;
    'indicatieStart'?: string | null;
    'indicatieEnd'?: string | null;
    'tariefContract'?: string | null;
    'minutenPerWeek'?: number | null;
    'urenPerWeek'?: number | null;
    'uurtarief'?: number | null;
    'wmoConsulentName'?: string | null;
    'wmoConsulentGemeente'?: string | null;
    'wmoConsulentEmail'?: string | null;
    'wmoConsulentPhone'?: string | null;
    'label'?: string | null;
    'opmerking'?: string | null;
    'isActive'?: boolean;
}


/**
 * All fields are optional. Only fields provided in the request body are updated; omitted fields remain unchanged. 
 */
export interface UpdateClientInsurancePolicyRequest {
    /**
     * Reference to insurer (from insurers table)
     */
    'insurerId'?: string;
    'verzekerdenummer'?: string | null;
    'soortVerzekering'?: string | null;
    'pakketCode'?: string | null;
    'pakketNaam'?: string | null;
    'labelCode'?: string | null;
    'labelNaam'?: string | null;
    'ingangsdatum'?: string | null;
    'einddatum'?: string | null;
    'isActive'?: boolean;
    'source'?: InsurancePolicySource;
    'lastVerifiedAt'?: number | null;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateClientRequest {
    /**
     * Client first name
     */
    'firstName'?: string | null;
    /**
     * Client last name
     */
    'lastName'?: string | null;
    /**
     * Name prefix (tussenvoegsel)
     */
    'prefix'?: string | null;
    /**
     * Client initials
     */
    'initials'?: string | null;
    /**
     * Client email address
     */
    'email'?: string | null;
    /**
     * Client phone number
     */
    'phone'?: string | null;
    /**
     * Street name only. The house number is `addressHouseNumber`; a caller that holds one composed line must split it before sending, because nothing here splits it for them.
     */
    'addressStreet'?: string | null;
    /**
     * House number, without its toevoeging
     */
    'addressHouseNumber'?: string | null;
    /**
     * Huisletter and huisnummertoevoeging together, as the zib carries them
     */
    'addressHouseNumberSuffix'?: string | null;
    /**
     * Postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * City
     */
    'addressCity'?: string | null;
    /**
     * Date of birth (YYYY-MM-DD)
     */
    'dateOfBirth'?: string | null;
    /**
     * Burgerservicenummer (9 digits).  A **changed** BSN must also pass the elfproef (11-check), which OpenAPI cannot express, and is refused with `400` and an `errors` entry naming `bsn` when it does not. A stored BSN this request does not change is accepted whatever it is — an edit to the address or the verzekering of a cliënt whose BSN predates that rule must not be blocked over a field nobody touched. 
     */
    'bsn'?: string | null;
    'gender'?: Gender | null;
    /**
     * Location ID where this client receives services. No operation of the public API lists locations yet.
     */
    'locationId'?: string | null;
    /**
     * Date of intake (YYYY-MM-DD)
     */
    'intakeDate'?: string | null;
    /**
     * Date of discharge (YYYY-MM-DD)
     */
    'dischargeDate'?: string | null;
    'idDocumentType'?: IdDocumentType | null;
    /**
     * Identity document number (Wabvpz). When both idDocumentType and idDocumentNumber are provided, the client\'s identity is marked as verified (idVerifiedAt/idVerifiedBy are stamped). No scan is stored.
     */
    'idDocumentNumber'?: string | null;
    /**
     * Level of the client\'s one standing warning. The two warning fields are one fact and are resolved together against what is stored, so a request naming neither leaves the warning untouched and one naming either re-decides both. OpenAPI 3.0 cannot express that, hence the prose:  - clearing `warningText` (sending null, or blank) removes the warning entirely,   level included — that is the only way to remove one; - `warningLevel` alone re-colours the stored text, and stores nothing when there   is no stored text; - `warningText` alone keeps the stored level, or WARNING when there was none. 
     */
    'warningLevel'?: ClientWarningLevel | null;
    /**
     * The client\'s one standing warning. Sending null or blank removes the warning. See `warningLevel` for how the two are resolved together. 
     */
    'warningText'?: string | null;
    /**
     * The language the client reads. Omitted leaves it unchanged.
     */
    'language'?: ClientLanguage;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateClientZorgvraagRequest {
    /**
     * Initial complaint or reason for referral
     */
    'aanmeldklacht'?: string | null;
    /**
     * Client\'s help request
     */
    'hulpvraag'?: string | null;
    /**
     * Goals and objectives
     */
    'doelen'?: Array<string>;
    /**
     * Type of help/support being offered
     */
    'hulpaanbod'?: string | null;
}
/**
 * Request to change the client portal visibility of a form assignment
 */
export interface UpdateFormAssignmentSharingRequest {
    /**
     * Whether the client may see the form in the portal
     */
    'shared': boolean;
    /**
     * Mail the client that a new form is waiting in the portal. Opt-in: absent means false, and nothing sends this mail otherwise. Only honoured when the request actually shares the assignment — hiding it, or re-sharing one that is already shared, sends nothing. The mail names the practice and never the form.  Deliberately on sharing rather than on `assignFormToClient`: an assigned form stays hidden from the portal until it is shared, so a mail at assignment would announce something the client cannot find. 
     */
    'notifyClient'?: boolean;
}
/**
 * Where the confirmed code came from: SUGGESTIE (what the woonplaats suggested), HANDMATIG (a code a person chose), TOEWIJZING (the gemeente of a received JW301), IMPORT (a period the practice confirmed in its previous EPD, migrated by an import). 
 */

export const VerantwoordelijkeGemeenteBron = {
    Suggestie: 'SUGGESTIE',
    Handmatig: 'HANDMATIG',
    Toewijzing: 'TOEWIJZING',
    Import: 'IMPORT'
} as const;

export type VerantwoordelijkeGemeenteBron = typeof VerantwoordelijkeGemeenteBron[keyof typeof VerantwoordelijkeGemeenteBron];


/**
 * The legal ground (#4695). Jeugdwet art. 1.1 woonplaats: WOONADRES (1°, the gezagsdrager\'s woonadres), VERBLIJF_VOORAFGAAND (2°, the woonplaats before a verblijf), GEBOORTE_MOEDER and WERKELIJK_VERBLIJF (3°/4°). Wmo 2015 art. 1.2.1: INGEZETENE (a), AANMELDGEMEENTE (b/c). 
 */

export const VerantwoordelijkeGemeenteGrondslag = {
    Woonadres: 'WOONADRES',
    VerblijfVoorafgaand: 'VERBLIJF_VOORAFGAAND',
    GeboorteMoeder: 'GEBOORTE_MOEDER',
    WerkelijkVerblijf: 'WERKELIJK_VERBLIJF',
    Ingezetene: 'INGEZETENE',
    Aanmeldgemeente: 'AANMELDGEMEENTE'
} as const;

export type VerantwoordelijkeGemeenteGrondslag = typeof VerantwoordelijkeGemeenteGrondslag[keyof typeof VerantwoordelijkeGemeenteGrondslag];


/**
 * The law a verantwoordelijke gemeente is responsible under.
 */

export const VerantwoordelijkeGemeenteRegime = {
    Jeugdwet: 'JEUGDWET',
    Wmo: 'WMO'
} as const;

export type VerantwoordelijkeGemeenteRegime = typeof VerantwoordelijkeGemeenteRegime[keyof typeof VerantwoordelijkeGemeenteRegime];


export interface VerantwoordelijkeGemeenteResponse {
    'id': string;
    'regime': VerantwoordelijkeGemeenteRegime;
    'gemeentecode': string;
    'geldigVanaf': string;
    /**
     * Inclusive; absent on the current period
     */
    'geldigTot'?: string | null;
    'grondslag': VerantwoordelijkeGemeenteGrondslag;
    'bron': VerantwoordelijkeGemeenteBron;
    /**
     * The user who confirmed the period
     */
    'bevestigdDoor': string;
    /**
     * When the period was confirmed, epoch milliseconds
     */
    'bevestigdOp': number;
}


export interface VerantwoordelijkeGemeenteSuggestieResponse {
    /**
     * UNIEK — the woonplaats lies in one gemeente; MEERDERE — the name fits woonplaatsen in several, all listed; ONBEKEND — no woonplaats by that name, or none recorded. 
     */
    'uitkomst': VerantwoordelijkeGemeenteSuggestieResponseUitkomstEnum;
    /**
     * The suggested code, or the candidates, sorted; empty when ONBEKEND
     */
    'gemeentecodes': Array<string>;
}

export const VerantwoordelijkeGemeenteSuggestieResponseUitkomstEnum = {
    Uniek: 'UNIEK',
    Meerdere: 'MEERDERE',
    Onbekend: 'ONBEKEND'
} as const;

export type VerantwoordelijkeGemeenteSuggestieResponseUitkomstEnum = typeof VerantwoordelijkeGemeenteSuggestieResponseUitkomstEnum[keyof typeof VerantwoordelijkeGemeenteSuggestieResponseUitkomstEnum];

/**
 * Why an AGB code resolved to no mailbox. Null when the lookup succeeded, and also when there was no code to look up.  `UNAVAILABLE` and `NOT_LISTED` need opposite responses and are deliberately distinct: the first is an adresboek outage and the second is a huisarts who is not on ZorgMail under this code. Neither is ever answered by addressing the code anyway. 
 */

export const ZorgMailAddressBookRefusal = {
    NotListed: 'NOT_LISTED',
    Unavailable: 'UNAVAILABLE',
    AgbCodeMalformed: 'AGB_CODE_MALFORMED'
} as const;

export type ZorgMailAddressBookRefusal = typeof ZorgMailAddressBookRefusal[keyof typeof ZorgMailAddressBookRefusal];


/**
 * Which of a record\'s two AGB codes the lookup matched. A `ZORGVERLENER` match names the person the code belongs to; a `PRAKTIJK` match names *someone at* the practice it belongs to, which is a weaker statement and worth showing the sender. 
 */

export const ZorgMailAgbMatch = {
    Zorgverlener: 'ZORGVERLENER',
    Praktijk: 'PRAKTIJK'
} as const;

export type ZorgMailAgbMatch = typeof ZorgMailAgbMatch[keyof typeof ZorgMailAgbMatch];


/**
 * One ZorgMail mailbox a letter to the huisarts could be addressed to.
 */
export interface ZorgMailRecipientCandidate {
    /**
     * The EDI address `RCPT TO` would name, as the adresboek states it.
     */
    'address': string;
    /**
     * The ZorgMail mailboxnummer, digits only. This is what routes the message *inside* the MEDVRI (`envelope.recipientidentifier`), which carries no domain because the EDIFACT character set has no `@`. 
     */
    'mailbox': string;
    /**
     * The recipient\'s name as the adresboek states it.
     */
    'name': string;
    'match': ZorgMailAgbMatch;
    /**
     * What the recipient is (huisarts, apotheek, …), as the adresboek states it — half of what makes a confirmation meaningful. 
     */
    'role'?: string | null;
    /**
     * Afdeling, where the mailbox belongs to one.
     */
    'department'?: string | null;
    /**
     * The practice or institution the mailbox belongs to.
     */
    'organization'?: string | null;
    'street'?: string | null;
    'postalCode'?: string | null;
    /**
     * Woonplaats. With `organization` this is what distinguishes two practices sharing a name.
     */
    'city'?: string | null;
}


/**
 * Everything a sender needs in order to say who a ZorgMail letter goes to — and nothing that decides it for them (#4121). 
 */
export interface ZorgMailRecipientResolution {
    'huisarts': HuisartsRecipientResolution;
    /**
     * Whether this organisation has a ZorgMail Account-ID at all (`getZorgMailConfig`). Answered here so the send dialog can say the practice is not set up *before* the behandelaar composes anything, rather than after they press versturen. 
     */
    'practiceConfigured': boolean;
    /**
     * The AGB code that was looked up — the one on the cliënt\'s huisarts, or the `agbCode` override. Null when the cliënt\'s huisarts carries no code and none was supplied, which is why `candidates` is then empty with no `lookupRefusal` to explain it. 
     */
    'agbCode'?: string | null;
    /**
     * Every ZorgMail mailbox that code reaches, most specific first — a `ZORGVERLENER` match before a `PRAKTIJK` one. More than one is normal rather than an error: a praktijk code is carried by every practitioner in the practice. 
     */
    'candidates': Array<ZorgMailRecipientCandidate>;
    'lookupRefusal'?: ZorgMailAddressBookRefusal | null;
}



/**
 * CareCircleApi - axios parameter creator
 */
export const CareCircleApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new care circle contact (zorgkring) for a client.
         * @summary Create care circle contact
         * @param {string} clientId Client UUID
         * @param {CreateCareCircleContactRequest} createCareCircleContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCareCircleContact: async (clientId: string, createCareCircleContactRequest: CreateCareCircleContactRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('createCareCircleContact', 'clientId', clientId)
            // verify required parameter 'createCareCircleContactRequest' is not null or undefined
            assertParamExists('createCareCircleContact', 'createCareCircleContactRequest', createCareCircleContactRequest)
            const localVarPath = `/api/v1/clients/{clientId}/care-circle`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createCareCircleContactRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft deletes a care circle contact by setting isActive to false.
         * @summary Delete care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCareCircleContact: async (clientId: string, contactId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteCareCircleContact', 'clientId', clientId)
            // verify required parameter 'contactId' is not null or undefined
            assertParamExists('deleteCareCircleContact', 'contactId', contactId)
            const localVarPath = `/api/v1/clients/{clientId}/care-circle/{contactId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"contactId"}}`, encodeURIComponent(String(contactId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a specific care circle contact by ID.
         * @summary Get care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCareCircleContact: async (clientId: string, contactId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getCareCircleContact', 'clientId', clientId)
            // verify required parameter 'contactId' is not null or undefined
            assertParamExists('getCareCircleContact', 'contactId', contactId)
            const localVarPath = `/api/v1/clients/{clientId}/care-circle/{contactId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"contactId"}}`, encodeURIComponent(String(contactId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves all active care circle contacts (zorgkring) for a client.
         * @summary List care circle contacts
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCareCircleContacts: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listCareCircleContacts', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/care-circle`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing care circle contact.
         * @summary Update care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {UpdateCareCircleContactRequest} updateCareCircleContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCareCircleContact: async (clientId: string, contactId: string, updateCareCircleContactRequest: UpdateCareCircleContactRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateCareCircleContact', 'clientId', clientId)
            // verify required parameter 'contactId' is not null or undefined
            assertParamExists('updateCareCircleContact', 'contactId', contactId)
            // verify required parameter 'updateCareCircleContactRequest' is not null or undefined
            assertParamExists('updateCareCircleContact', 'updateCareCircleContactRequest', updateCareCircleContactRequest)
            const localVarPath = `/api/v1/clients/{clientId}/care-circle/{contactId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"contactId"}}`, encodeURIComponent(String(contactId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateCareCircleContactRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CareCircleApi - functional programming interface
 */
export const CareCircleApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = CareCircleApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new care circle contact (zorgkring) for a client.
         * @summary Create care circle contact
         * @param {string} clientId Client UUID
         * @param {CreateCareCircleContactRequest} createCareCircleContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createCareCircleContact(clientId: string, createCareCircleContactRequest: CreateCareCircleContactRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CareCircleContactResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createCareCircleContact(clientId, createCareCircleContactRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CareCircleApi.createCareCircleContact']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft deletes a care circle contact by setting isActive to false.
         * @summary Delete care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteCareCircleContact(clientId: string, contactId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteCareCircleContact(clientId, contactId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CareCircleApi.deleteCareCircleContact']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a specific care circle contact by ID.
         * @summary Get care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCareCircleContact(clientId: string, contactId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CareCircleContactResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCareCircleContact(clientId, contactId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CareCircleApi.getCareCircleContact']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves all active care circle contacts (zorgkring) for a client.
         * @summary List care circle contacts
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listCareCircleContacts(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<CareCircleContactResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listCareCircleContacts(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CareCircleApi.listCareCircleContacts']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing care circle contact.
         * @summary Update care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {UpdateCareCircleContactRequest} updateCareCircleContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCareCircleContact(clientId: string, contactId: string, updateCareCircleContactRequest: UpdateCareCircleContactRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CareCircleContactResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCareCircleContact(clientId, contactId, updateCareCircleContactRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['CareCircleApi.updateCareCircleContact']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * CareCircleApi - factory interface
 */
export const CareCircleApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = CareCircleApiFp(configuration)
    return {
        /**
         * Creates a new care circle contact (zorgkring) for a client.
         * @summary Create care circle contact
         * @param {string} clientId Client UUID
         * @param {CreateCareCircleContactRequest} createCareCircleContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createCareCircleContact(clientId: string, createCareCircleContactRequest: CreateCareCircleContactRequest, options?: RawAxiosRequestConfig): AxiosPromise<CareCircleContactResponse> {
            return localVarFp.createCareCircleContact(clientId, createCareCircleContactRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft deletes a care circle contact by setting isActive to false.
         * @summary Delete care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteCareCircleContact(clientId: string, contactId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteCareCircleContact(clientId, contactId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a specific care circle contact by ID.
         * @summary Get care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCareCircleContact(clientId: string, contactId: string, options?: RawAxiosRequestConfig): AxiosPromise<CareCircleContactResponse> {
            return localVarFp.getCareCircleContact(clientId, contactId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves all active care circle contacts (zorgkring) for a client.
         * @summary List care circle contacts
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listCareCircleContacts(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<CareCircleContactResponse>> {
            return localVarFp.listCareCircleContacts(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing care circle contact.
         * @summary Update care circle contact
         * @param {string} clientId Client UUID
         * @param {string} contactId Care circle contact UUID
         * @param {UpdateCareCircleContactRequest} updateCareCircleContactRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCareCircleContact(clientId: string, contactId: string, updateCareCircleContactRequest: UpdateCareCircleContactRequest, options?: RawAxiosRequestConfig): AxiosPromise<CareCircleContactResponse> {
            return localVarFp.updateCareCircleContact(clientId, contactId, updateCareCircleContactRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CareCircleApi - object-oriented interface
 */
export class CareCircleApi extends BaseAPI {
    /**
     * Creates a new care circle contact (zorgkring) for a client.
     * @summary Create care circle contact
     * @param {string} clientId Client UUID
     * @param {CreateCareCircleContactRequest} createCareCircleContactRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createCareCircleContact(clientId: string, createCareCircleContactRequest: CreateCareCircleContactRequest, options?: RawAxiosRequestConfig) {
        return CareCircleApiFp(this.configuration).createCareCircleContact(clientId, createCareCircleContactRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft deletes a care circle contact by setting isActive to false.
     * @summary Delete care circle contact
     * @param {string} clientId Client UUID
     * @param {string} contactId Care circle contact UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteCareCircleContact(clientId: string, contactId: string, options?: RawAxiosRequestConfig) {
        return CareCircleApiFp(this.configuration).deleteCareCircleContact(clientId, contactId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a specific care circle contact by ID.
     * @summary Get care circle contact
     * @param {string} clientId Client UUID
     * @param {string} contactId Care circle contact UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCareCircleContact(clientId: string, contactId: string, options?: RawAxiosRequestConfig) {
        return CareCircleApiFp(this.configuration).getCareCircleContact(clientId, contactId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves all active care circle contacts (zorgkring) for a client.
     * @summary List care circle contacts
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listCareCircleContacts(clientId: string, options?: RawAxiosRequestConfig) {
        return CareCircleApiFp(this.configuration).listCareCircleContacts(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing care circle contact.
     * @summary Update care circle contact
     * @param {string} clientId Client UUID
     * @param {string} contactId Care circle contact UUID
     * @param {UpdateCareCircleContactRequest} updateCareCircleContactRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateCareCircleContact(clientId: string, contactId: string, updateCareCircleContactRequest: UpdateCareCircleContactRequest, options?: RawAxiosRequestConfig) {
        return CareCircleApiFp(this.configuration).updateCareCircleContact(clientId, contactId, updateCareCircleContactRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientActivityApi - axios parameter creator
 */
export const ClientActivityApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns a paginated activity feed for the dossier dashboard, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  **Authorization:** - Caller must be able to read the client (existing client-read authorization). - The `includeViews` toggle is gated on the `audit.read` permission. Without   that permission, requesting `includeViews=true` returns 403 — the toggle   is gated, not the endpoint as a whole.  **Filtering:** - When `includeViews=false` (default), excludes access events (VIEW, LIST,   DOWNLOAD, EXPORT). - When `includeViews=true`, includes both mutation and access events. - `includeApiKoppelingen=false` leaves out what API-koppelingen did, which is what the   dossier widget asks for by default, as the audit log screen does. Presentation only:   the rows stay recorded.  **Redaction tier:** - `ipAddress` and `userAgent` are only populated when the caller has   `audit.read`; otherwise omitted from the response. Sensitive fields   inside `summary`/`changes` are already redacted at write time via   `trackSensitive()`.  Results are ordered by `occurredAt DESC` (most recent first). 
         * @summary Get client activity feed
         * @param {string} clientId Client UUID
         * @param {boolean} [includeViews] When true, includes access events (VIEW, LIST, DOWNLOAD, EXPORT). Requires the &#x60;audit.read&#x60; permission. 
         * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the feed shows what people did. 
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientActivity: async (clientId: string, includeViews?: boolean, includeApiKoppelingen?: boolean, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientActivity', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/activity`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (includeViews !== undefined) {
                localVarQueryParameter['includeViews'] = includeViews;
            }

            if (includeApiKoppelingen !== undefined) {
                localVarQueryParameter['includeApiKoppelingen'] = includeApiKoppelingen;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated, semantically typed activity timeline for the client dossier, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  Unlike `/activity` — which exposes raw audit rows for the audit trail — this endpoint only surfaces the handful of dossier events that are meaningful to a behandelaar, each translated into a `type`, a Dutch `title` and a Dutch `description`. Audit rows without a semantic mapping are omitted rather than rendered as generic strings, so a page may hold fewer than `size` events.  Access events (VIEW, LIST, DOWNLOAD, EXPORT) never appear here.  **Authorization:** caller must be able to read the client (same rule as `/activity`).  Results are ordered by `occurredAt DESC` (most recent first). 
         * @summary Get client activity timeline
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientTimeline: async (clientId: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientTimeline', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/timeline`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientActivityApi - functional programming interface
 */
export const ClientActivityApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientActivityApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns a paginated activity feed for the dossier dashboard, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  **Authorization:** - Caller must be able to read the client (existing client-read authorization). - The `includeViews` toggle is gated on the `audit.read` permission. Without   that permission, requesting `includeViews=true` returns 403 — the toggle   is gated, not the endpoint as a whole.  **Filtering:** - When `includeViews=false` (default), excludes access events (VIEW, LIST,   DOWNLOAD, EXPORT). - When `includeViews=true`, includes both mutation and access events. - `includeApiKoppelingen=false` leaves out what API-koppelingen did, which is what the   dossier widget asks for by default, as the audit log screen does. Presentation only:   the rows stay recorded.  **Redaction tier:** - `ipAddress` and `userAgent` are only populated when the caller has   `audit.read`; otherwise omitted from the response. Sensitive fields   inside `summary`/`changes` are already redacted at write time via   `trackSensitive()`.  Results are ordered by `occurredAt DESC` (most recent first). 
         * @summary Get client activity feed
         * @param {string} clientId Client UUID
         * @param {boolean} [includeViews] When true, includes access events (VIEW, LIST, DOWNLOAD, EXPORT). Requires the &#x60;audit.read&#x60; permission. 
         * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the feed shows what people did. 
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientActivity(clientId: string, includeViews?: boolean, includeApiKoppelingen?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientActivityPage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientActivity(clientId, includeViews, includeApiKoppelingen, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientActivityApi.getClientActivity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated, semantically typed activity timeline for the client dossier, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  Unlike `/activity` — which exposes raw audit rows for the audit trail — this endpoint only surfaces the handful of dossier events that are meaningful to a behandelaar, each translated into a `type`, a Dutch `title` and a Dutch `description`. Audit rows without a semantic mapping are omitted rather than rendered as generic strings, so a page may hold fewer than `size` events.  Access events (VIEW, LIST, DOWNLOAD, EXPORT) never appear here.  **Authorization:** caller must be able to read the client (same rule as `/activity`).  Results are ordered by `occurredAt DESC` (most recent first). 
         * @summary Get client activity timeline
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientTimeline(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientTimelinePage>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientTimeline(clientId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientActivityApi.getClientTimeline']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientActivityApi - factory interface
 */
export const ClientActivityApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientActivityApiFp(configuration)
    return {
        /**
         * Returns a paginated activity feed for the dossier dashboard, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  **Authorization:** - Caller must be able to read the client (existing client-read authorization). - The `includeViews` toggle is gated on the `audit.read` permission. Without   that permission, requesting `includeViews=true` returns 403 — the toggle   is gated, not the endpoint as a whole.  **Filtering:** - When `includeViews=false` (default), excludes access events (VIEW, LIST,   DOWNLOAD, EXPORT). - When `includeViews=true`, includes both mutation and access events. - `includeApiKoppelingen=false` leaves out what API-koppelingen did, which is what the   dossier widget asks for by default, as the audit log screen does. Presentation only:   the rows stay recorded.  **Redaction tier:** - `ipAddress` and `userAgent` are only populated when the caller has   `audit.read`; otherwise omitted from the response. Sensitive fields   inside `summary`/`changes` are already redacted at write time via   `trackSensitive()`.  Results are ordered by `occurredAt DESC` (most recent first). 
         * @summary Get client activity feed
         * @param {string} clientId Client UUID
         * @param {boolean} [includeViews] When true, includes access events (VIEW, LIST, DOWNLOAD, EXPORT). Requires the &#x60;audit.read&#x60; permission. 
         * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the feed shows what people did. 
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientActivity(clientId: string, includeViews?: boolean, includeApiKoppelingen?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<ClientActivityPage> {
            return localVarFp.getClientActivity(clientId, includeViews, includeApiKoppelingen, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated, semantically typed activity timeline for the client dossier, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  Unlike `/activity` — which exposes raw audit rows for the audit trail — this endpoint only surfaces the handful of dossier events that are meaningful to a behandelaar, each translated into a `type`, a Dutch `title` and a Dutch `description`. Audit rows without a semantic mapping are omitted rather than rendered as generic strings, so a page may hold fewer than `size` events.  Access events (VIEW, LIST, DOWNLOAD, EXPORT) never appear here.  **Authorization:** caller must be able to read the client (same rule as `/activity`).  Results are ordered by `occurredAt DESC` (most recent first). 
         * @summary Get client activity timeline
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientTimeline(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<ClientTimelinePage> {
            return localVarFp.getClientTimeline(clientId, page, size, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientActivityApi - object-oriented interface
 */
export class ClientActivityApi extends BaseAPI {
    /**
     * Returns a paginated activity feed for the dossier dashboard, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  **Authorization:** - Caller must be able to read the client (existing client-read authorization). - The `includeViews` toggle is gated on the `audit.read` permission. Without   that permission, requesting `includeViews=true` returns 403 — the toggle   is gated, not the endpoint as a whole.  **Filtering:** - When `includeViews=false` (default), excludes access events (VIEW, LIST,   DOWNLOAD, EXPORT). - When `includeViews=true`, includes both mutation and access events. - `includeApiKoppelingen=false` leaves out what API-koppelingen did, which is what the   dossier widget asks for by default, as the audit log screen does. Presentation only:   the rows stay recorded.  **Redaction tier:** - `ipAddress` and `userAgent` are only populated when the caller has   `audit.read`; otherwise omitted from the response. Sensitive fields   inside `summary`/`changes` are already redacted at write time via   `trackSensitive()`.  Results are ordered by `occurredAt DESC` (most recent first). 
     * @summary Get client activity feed
     * @param {string} clientId Client UUID
     * @param {boolean} [includeViews] When true, includes access events (VIEW, LIST, DOWNLOAD, EXPORT). Requires the &#x60;audit.read&#x60; permission. 
     * @param {boolean} [includeApiKoppelingen] Include what API-koppelingen did. &#x60;false&#x60; leaves them out, so the feed shows what people did. 
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientActivity(clientId: string, includeViews?: boolean, includeApiKoppelingen?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return ClientActivityApiFp(this.configuration).getClientActivity(clientId, includeViews, includeApiKoppelingen, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated, semantically typed activity timeline for the client dossier, sourced from the unified audit log filtered by `subjectClientId = clientId` AND the caller\'s organization.  Unlike `/activity` — which exposes raw audit rows for the audit trail — this endpoint only surfaces the handful of dossier events that are meaningful to a behandelaar, each translated into a `type`, a Dutch `title` and a Dutch `description`. Audit rows without a semantic mapping are omitted rather than rendered as generic strings, so a page may hold fewer than `size` events.  Access events (VIEW, LIST, DOWNLOAD, EXPORT) never appear here.  **Authorization:** caller must be able to read the client (same rule as `/activity`).  Results are ordered by `occurredAt DESC` (most recent first). 
     * @summary Get client activity timeline
     * @param {string} clientId Client UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientTimeline(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return ClientActivityApiFp(this.configuration).getClientTimeline(clientId, page, size, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientAiRecapApi - axios parameter creator
 */
export const ClientAiRecapApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns an AI-generated summary of recent developments for a client. The summary incorporates recent appointments, form submissions, and transcriptions. Results are cached and regenerated when new data is available. 
         * @summary Get AI-generated client recap
         * @param {string} clientId Client UUID
         * @param {boolean} [forceRegenerate] Force regeneration of the recap, ignoring cache
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientAiRecap: async (clientId: string, forceRegenerate?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientAiRecap', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/ai-recap`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (forceRegenerate !== undefined) {
                localVarQueryParameter['forceRegenerate'] = forceRegenerate;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientAiRecapApi - functional programming interface
 */
export const ClientAiRecapApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientAiRecapApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns an AI-generated summary of recent developments for a client. The summary incorporates recent appointments, form submissions, and transcriptions. Results are cached and regenerated when new data is available. 
         * @summary Get AI-generated client recap
         * @param {string} clientId Client UUID
         * @param {boolean} [forceRegenerate] Force regeneration of the recap, ignoring cache
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientAiRecap(clientId: string, forceRegenerate?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AiRecapResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientAiRecap(clientId, forceRegenerate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientAiRecapApi.getClientAiRecap']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientAiRecapApi - factory interface
 */
export const ClientAiRecapApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientAiRecapApiFp(configuration)
    return {
        /**
         * Returns an AI-generated summary of recent developments for a client. The summary incorporates recent appointments, form submissions, and transcriptions. Results are cached and regenerated when new data is available. 
         * @summary Get AI-generated client recap
         * @param {string} clientId Client UUID
         * @param {boolean} [forceRegenerate] Force regeneration of the recap, ignoring cache
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientAiRecap(clientId: string, forceRegenerate?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<AiRecapResponse> {
            return localVarFp.getClientAiRecap(clientId, forceRegenerate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientAiRecapApi - object-oriented interface
 */
export class ClientAiRecapApi extends BaseAPI {
    /**
     * Returns an AI-generated summary of recent developments for a client. The summary incorporates recent appointments, form submissions, and transcriptions. Results are cached and regenerated when new data is available. 
     * @summary Get AI-generated client recap
     * @param {string} clientId Client UUID
     * @param {boolean} [forceRegenerate] Force regeneration of the recap, ignoring cache
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientAiRecap(clientId: string, forceRegenerate?: boolean, options?: RawAxiosRequestConfig) {
        return ClientAiRecapApiFp(this.configuration).getClientAiRecap(clientId, forceRegenerate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientAvatarApi - axios parameter creator
 */
export const ClientAvatarApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Removes the client\'s avatar image.
         * @summary Delete client avatar
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientAvatar: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClientAvatar', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/avatar`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves the client\'s avatar image. Streams the image bytes with the correct Content-Type header.
         * @summary Get client avatar
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientAvatar: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientAvatar', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/avatar`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'image/jpeg,image/png,image/webp,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads a new avatar image for the client. Accepts JPEG, PNG, or WebP images up to 5MB.
         * @summary Upload client avatar
         * @param {string} clientId Client UUID
         * @param {File} file Image file (JPEG, PNG, or WebP, max 5MB)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadClientAvatar: async (clientId: string, file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('uploadClientAvatar', 'clientId', clientId)
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadClientAvatar', 'file', file)
            const localVarPath = `/api/v1/clients/{clientId}/avatar`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientAvatarApi - functional programming interface
 */
export const ClientAvatarApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientAvatarApiAxiosParamCreator(configuration)
    return {
        /**
         * Removes the client\'s avatar image.
         * @summary Delete client avatar
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientAvatar(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientAvatar(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientAvatarApi.deleteClientAvatar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves the client\'s avatar image. Streams the image bytes with the correct Content-Type header.
         * @summary Get client avatar
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientAvatar(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientAvatar(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientAvatarApi.getClientAvatar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads a new avatar image for the client. Accepts JPEG, PNG, or WebP images up to 5MB.
         * @summary Upload client avatar
         * @param {string} clientId Client UUID
         * @param {File} file Image file (JPEG, PNG, or WebP, max 5MB)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadClientAvatar(clientId: string, file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientAvatarResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadClientAvatar(clientId, file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientAvatarApi.uploadClientAvatar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientAvatarApi - factory interface
 */
export const ClientAvatarApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientAvatarApiFp(configuration)
    return {
        /**
         * Removes the client\'s avatar image.
         * @summary Delete client avatar
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientAvatar(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientAvatar(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves the client\'s avatar image. Streams the image bytes with the correct Content-Type header.
         * @summary Get client avatar
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientAvatar(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.getClientAvatar(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads a new avatar image for the client. Accepts JPEG, PNG, or WebP images up to 5MB.
         * @summary Upload client avatar
         * @param {string} clientId Client UUID
         * @param {File} file Image file (JPEG, PNG, or WebP, max 5MB)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadClientAvatar(clientId: string, file: File, options?: RawAxiosRequestConfig): AxiosPromise<ClientAvatarResponse> {
            return localVarFp.uploadClientAvatar(clientId, file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientAvatarApi - object-oriented interface
 */
export class ClientAvatarApi extends BaseAPI {
    /**
     * Removes the client\'s avatar image.
     * @summary Delete client avatar
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientAvatar(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientAvatarApiFp(this.configuration).deleteClientAvatar(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves the client\'s avatar image. Streams the image bytes with the correct Content-Type header.
     * @summary Get client avatar
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientAvatar(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientAvatarApiFp(this.configuration).getClientAvatar(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads a new avatar image for the client. Accepts JPEG, PNG, or WebP images up to 5MB.
     * @summary Upload client avatar
     * @param {string} clientId Client UUID
     * @param {File} file Image file (JPEG, PNG, or WebP, max 5MB)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadClientAvatar(clientId: string, file: File, options?: RawAxiosRequestConfig) {
        return ClientAvatarApiFp(this.configuration).uploadClientAvatar(clientId, file, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientCommentsApi - axios parameter creator
 */
export const ClientCommentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new comment (opmerking) for a client.
         * @summary Create client comment
         * @param {string} clientId Client UUID
         * @param {CreateClientCommentRequest} createClientCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientComment: async (clientId: string, createClientCommentRequest: CreateClientCommentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('createClientComment', 'clientId', clientId)
            // verify required parameter 'createClientCommentRequest' is not null or undefined
            assertParamExists('createClientComment', 'createClientCommentRequest', createClientCommentRequest)
            const localVarPath = `/api/v1/clients/{clientId}/comments`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createClientCommentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft deletes a comment. Only the author can delete their own comments.
         * @summary Delete client comment
         * @param {string} clientId Client UUID
         * @param {string} commentId Comment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientComment: async (clientId: string, commentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClientComment', 'clientId', clientId)
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('deleteClientComment', 'commentId', commentId)
            const localVarPath = `/api/v1/clients/{clientId}/comments/{commentId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"commentId"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves all comments (opmerkingen) for a client, sorted by creation date (newest first).
         * @summary List client comments
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientComments: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientComments', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/comments`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing comment. Only the author can update their own comments.
         * @summary Update client comment
         * @param {string} clientId Client UUID
         * @param {string} commentId Comment UUID
         * @param {UpdateClientCommentRequest} updateClientCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientComment: async (clientId: string, commentId: string, updateClientCommentRequest: UpdateClientCommentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientComment', 'clientId', clientId)
            // verify required parameter 'commentId' is not null or undefined
            assertParamExists('updateClientComment', 'commentId', commentId)
            // verify required parameter 'updateClientCommentRequest' is not null or undefined
            assertParamExists('updateClientComment', 'updateClientCommentRequest', updateClientCommentRequest)
            const localVarPath = `/api/v1/clients/{clientId}/comments/{commentId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"commentId"}}`, encodeURIComponent(String(commentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientCommentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientCommentsApi - functional programming interface
 */
export const ClientCommentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientCommentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new comment (opmerking) for a client.
         * @summary Create client comment
         * @param {string} clientId Client UUID
         * @param {CreateClientCommentRequest} createClientCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createClientComment(clientId: string, createClientCommentRequest: CreateClientCommentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientCommentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createClientComment(clientId, createClientCommentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientCommentsApi.createClientComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft deletes a comment. Only the author can delete their own comments.
         * @summary Delete client comment
         * @param {string} clientId Client UUID
         * @param {string} commentId Comment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientComment(clientId: string, commentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientComment(clientId, commentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientCommentsApi.deleteClientComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves all comments (opmerkingen) for a client, sorted by creation date (newest first).
         * @summary List client comments
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientComments(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ClientCommentResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientComments(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientCommentsApi.listClientComments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing comment. Only the author can update their own comments.
         * @summary Update client comment
         * @param {string} clientId Client UUID
         * @param {string} commentId Comment UUID
         * @param {UpdateClientCommentRequest} updateClientCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientComment(clientId: string, commentId: string, updateClientCommentRequest: UpdateClientCommentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientCommentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientComment(clientId, commentId, updateClientCommentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientCommentsApi.updateClientComment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientCommentsApi - factory interface
 */
export const ClientCommentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientCommentsApiFp(configuration)
    return {
        /**
         * Creates a new comment (opmerking) for a client.
         * @summary Create client comment
         * @param {string} clientId Client UUID
         * @param {CreateClientCommentRequest} createClientCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientComment(clientId: string, createClientCommentRequest: CreateClientCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientCommentResponse> {
            return localVarFp.createClientComment(clientId, createClientCommentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft deletes a comment. Only the author can delete their own comments.
         * @summary Delete client comment
         * @param {string} clientId Client UUID
         * @param {string} commentId Comment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientComment(clientId: string, commentId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientComment(clientId, commentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves all comments (opmerkingen) for a client, sorted by creation date (newest first).
         * @summary List client comments
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientComments(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ClientCommentResponse>> {
            return localVarFp.listClientComments(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing comment. Only the author can update their own comments.
         * @summary Update client comment
         * @param {string} clientId Client UUID
         * @param {string} commentId Comment UUID
         * @param {UpdateClientCommentRequest} updateClientCommentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientComment(clientId: string, commentId: string, updateClientCommentRequest: UpdateClientCommentRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientCommentResponse> {
            return localVarFp.updateClientComment(clientId, commentId, updateClientCommentRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientCommentsApi - object-oriented interface
 */
export class ClientCommentsApi extends BaseAPI {
    /**
     * Creates a new comment (opmerking) for a client.
     * @summary Create client comment
     * @param {string} clientId Client UUID
     * @param {CreateClientCommentRequest} createClientCommentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createClientComment(clientId: string, createClientCommentRequest: CreateClientCommentRequest, options?: RawAxiosRequestConfig) {
        return ClientCommentsApiFp(this.configuration).createClientComment(clientId, createClientCommentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft deletes a comment. Only the author can delete their own comments.
     * @summary Delete client comment
     * @param {string} clientId Client UUID
     * @param {string} commentId Comment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientComment(clientId: string, commentId: string, options?: RawAxiosRequestConfig) {
        return ClientCommentsApiFp(this.configuration).deleteClientComment(clientId, commentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves all comments (opmerkingen) for a client, sorted by creation date (newest first).
     * @summary List client comments
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientComments(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientCommentsApiFp(this.configuration).listClientComments(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing comment. Only the author can update their own comments.
     * @summary Update client comment
     * @param {string} clientId Client UUID
     * @param {string} commentId Comment UUID
     * @param {UpdateClientCommentRequest} updateClientCommentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientComment(clientId: string, commentId: string, updateClientCommentRequest: UpdateClientCommentRequest, options?: RawAxiosRequestConfig) {
        return ClientCommentsApiFp(this.configuration).updateClientComment(clientId, commentId, updateClientCommentRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientFinancieringApi - axios parameter creator
 */
export const ClientFinancieringApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new insurance policy under the client\'s financiering record. Creates the financiering record on the fly if none exists yet. 
         * @summary Create insurance policy
         * @param {string} clientId Client UUID
         * @param {CreateClientInsurancePolicyRequest} createClientInsurancePolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientInsurancePolicy: async (clientId: string, createClientInsurancePolicyRequest: CreateClientInsurancePolicyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('createClientInsurancePolicy', 'clientId', clientId)
            // verify required parameter 'createClientInsurancePolicyRequest' is not null or undefined
            assertParamExists('createClientInsurancePolicy', 'createClientInsurancePolicyRequest', createClientInsurancePolicyRequest)
            const localVarPath = `/api/v1/clients/{clientId}/financiering/policies`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createClientInsurancePolicyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes an insurance policy from the client\'s financiering record.
         * @summary Delete insurance policy
         * @param {string} clientId Client UUID
         * @param {string} policyId Insurance policy UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientInsurancePolicy: async (clientId: string, policyId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClientInsurancePolicy', 'clientId', clientId)
            // verify required parameter 'policyId' is not null or undefined
            assertParamExists('deleteClientInsurancePolicy', 'policyId', policyId)
            const localVarPath = `/api/v1/clients/{clientId}/financiering/policies/{policyId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"policyId"}}`, encodeURIComponent(String(policyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves financing (financiering) data for a client including insurance information.
         * @summary Get client financing data
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientFinanciering: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientFinanciering', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/financiering`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Verifies the client\'s insurance status with VECOZO COV using the client\'s BSN and date of birth, then upserts the returned policies on the client\'s financiering record.  Matching is by `(insurerId, verzekerdenummer)`: - Existing matched policies are updated from the COV response. - Unmatched COV results become new policies with `source=COV`. - Existing `source=COV` policies that are missing from the response are   deactivated (`isActive=false`); they are never deleted, so the   history is preserved. - `source=MANUAL` policies are not touched.  A COV coverage whose UZOVI code is not in the insurer catalogue cannot be stored and is reported in `skippedCoverages`. Such a run leaves the existing policies alone — nothing is deactivated — because an absent insurer is a gap in our reference data, not proof the client lost the coverage.  Every touched row gets `lastVerifiedAt` stamped to the current time. 
         * @summary Sync insurance policies from VECOZO COV
         * @param {string} clientId Client UUID
         * @param {string} [peildatum] Reference date for the COV check. Defaults to today.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncClientInsurancePoliciesFromCov: async (clientId: string, peildatum?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('syncClientInsurancePoliciesFromCov', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/financiering/policies/sync-from-cov`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (peildatum !== undefined) {
                localVarQueryParameter['peildatum'] = (peildatum as any instanceof Date) ?
                    (peildatum as any).toISOString().substring(0,10) :
                    peildatum;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates financing (financiering) data for a client.
         * @summary Update client financing data
         * @param {string} clientId Client UUID
         * @param {UpdateClientFinancieringRequest} updateClientFinancieringRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientFinanciering: async (clientId: string, updateClientFinancieringRequest: UpdateClientFinancieringRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientFinanciering', 'clientId', clientId)
            // verify required parameter 'updateClientFinancieringRequest' is not null or undefined
            assertParamExists('updateClientFinanciering', 'updateClientFinancieringRequest', updateClientFinancieringRequest)
            const localVarPath = `/api/v1/clients/{clientId}/financiering`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientFinancieringRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates fields on an existing insurance policy. Only provided fields are changed.
         * @summary Update insurance policy
         * @param {string} clientId Client UUID
         * @param {string} policyId Insurance policy UUID
         * @param {UpdateClientInsurancePolicyRequest} updateClientInsurancePolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientInsurancePolicy: async (clientId: string, policyId: string, updateClientInsurancePolicyRequest: UpdateClientInsurancePolicyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientInsurancePolicy', 'clientId', clientId)
            // verify required parameter 'policyId' is not null or undefined
            assertParamExists('updateClientInsurancePolicy', 'policyId', policyId)
            // verify required parameter 'updateClientInsurancePolicyRequest' is not null or undefined
            assertParamExists('updateClientInsurancePolicy', 'updateClientInsurancePolicyRequest', updateClientInsurancePolicyRequest)
            const localVarPath = `/api/v1/clients/{clientId}/financiering/policies/{policyId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"policyId"}}`, encodeURIComponent(String(policyId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientInsurancePolicyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientFinancieringApi - functional programming interface
 */
export const ClientFinancieringApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientFinancieringApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new insurance policy under the client\'s financiering record. Creates the financiering record on the fly if none exists yet. 
         * @summary Create insurance policy
         * @param {string} clientId Client UUID
         * @param {CreateClientInsurancePolicyRequest} createClientInsurancePolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createClientInsurancePolicy(clientId: string, createClientInsurancePolicyRequest: CreateClientInsurancePolicyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientInsurancePolicy>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createClientInsurancePolicy(clientId, createClientInsurancePolicyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancieringApi.createClientInsurancePolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes an insurance policy from the client\'s financiering record.
         * @summary Delete insurance policy
         * @param {string} clientId Client UUID
         * @param {string} policyId Insurance policy UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientInsurancePolicy(clientId: string, policyId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientInsurancePolicy(clientId, policyId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancieringApi.deleteClientInsurancePolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves financing (financiering) data for a client including insurance information.
         * @summary Get client financing data
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientFinanciering(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFinancieringResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientFinanciering(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancieringApi.getClientFinanciering']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Verifies the client\'s insurance status with VECOZO COV using the client\'s BSN and date of birth, then upserts the returned policies on the client\'s financiering record.  Matching is by `(insurerId, verzekerdenummer)`: - Existing matched policies are updated from the COV response. - Unmatched COV results become new policies with `source=COV`. - Existing `source=COV` policies that are missing from the response are   deactivated (`isActive=false`); they are never deleted, so the   history is preserved. - `source=MANUAL` policies are not touched.  A COV coverage whose UZOVI code is not in the insurer catalogue cannot be stored and is reported in `skippedCoverages`. Such a run leaves the existing policies alone — nothing is deactivated — because an absent insurer is a gap in our reference data, not proof the client lost the coverage.  Every touched row gets `lastVerifiedAt` stamped to the current time. 
         * @summary Sync insurance policies from VECOZO COV
         * @param {string} clientId Client UUID
         * @param {string} [peildatum] Reference date for the COV check. Defaults to today.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async syncClientInsurancePoliciesFromCov(clientId: string, peildatum?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CovSyncResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.syncClientInsurancePoliciesFromCov(clientId, peildatum, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancieringApi.syncClientInsurancePoliciesFromCov']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates financing (financiering) data for a client.
         * @summary Update client financing data
         * @param {string} clientId Client UUID
         * @param {UpdateClientFinancieringRequest} updateClientFinancieringRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientFinanciering(clientId: string, updateClientFinancieringRequest: UpdateClientFinancieringRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFinancieringResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientFinanciering(clientId, updateClientFinancieringRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancieringApi.updateClientFinanciering']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates fields on an existing insurance policy. Only provided fields are changed.
         * @summary Update insurance policy
         * @param {string} clientId Client UUID
         * @param {string} policyId Insurance policy UUID
         * @param {UpdateClientInsurancePolicyRequest} updateClientInsurancePolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientInsurancePolicy(clientId: string, policyId: string, updateClientInsurancePolicyRequest: UpdateClientInsurancePolicyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientInsurancePolicy>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientInsurancePolicy(clientId, policyId, updateClientInsurancePolicyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancieringApi.updateClientInsurancePolicy']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientFinancieringApi - factory interface
 */
export const ClientFinancieringApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientFinancieringApiFp(configuration)
    return {
        /**
         * Creates a new insurance policy under the client\'s financiering record. Creates the financiering record on the fly if none exists yet. 
         * @summary Create insurance policy
         * @param {string} clientId Client UUID
         * @param {CreateClientInsurancePolicyRequest} createClientInsurancePolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientInsurancePolicy(clientId: string, createClientInsurancePolicyRequest: CreateClientInsurancePolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientInsurancePolicy> {
            return localVarFp.createClientInsurancePolicy(clientId, createClientInsurancePolicyRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes an insurance policy from the client\'s financiering record.
         * @summary Delete insurance policy
         * @param {string} clientId Client UUID
         * @param {string} policyId Insurance policy UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientInsurancePolicy(clientId: string, policyId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientInsurancePolicy(clientId, policyId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves financing (financiering) data for a client including insurance information.
         * @summary Get client financing data
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientFinanciering(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientFinancieringResponse> {
            return localVarFp.getClientFinanciering(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Verifies the client\'s insurance status with VECOZO COV using the client\'s BSN and date of birth, then upserts the returned policies on the client\'s financiering record.  Matching is by `(insurerId, verzekerdenummer)`: - Existing matched policies are updated from the COV response. - Unmatched COV results become new policies with `source=COV`. - Existing `source=COV` policies that are missing from the response are   deactivated (`isActive=false`); they are never deleted, so the   history is preserved. - `source=MANUAL` policies are not touched.  A COV coverage whose UZOVI code is not in the insurer catalogue cannot be stored and is reported in `skippedCoverages`. Such a run leaves the existing policies alone — nothing is deactivated — because an absent insurer is a gap in our reference data, not proof the client lost the coverage.  Every touched row gets `lastVerifiedAt` stamped to the current time. 
         * @summary Sync insurance policies from VECOZO COV
         * @param {string} clientId Client UUID
         * @param {string} [peildatum] Reference date for the COV check. Defaults to today.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncClientInsurancePoliciesFromCov(clientId: string, peildatum?: string, options?: RawAxiosRequestConfig): AxiosPromise<CovSyncResponse> {
            return localVarFp.syncClientInsurancePoliciesFromCov(clientId, peildatum, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates financing (financiering) data for a client.
         * @summary Update client financing data
         * @param {string} clientId Client UUID
         * @param {UpdateClientFinancieringRequest} updateClientFinancieringRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientFinanciering(clientId: string, updateClientFinancieringRequest: UpdateClientFinancieringRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientFinancieringResponse> {
            return localVarFp.updateClientFinanciering(clientId, updateClientFinancieringRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates fields on an existing insurance policy. Only provided fields are changed.
         * @summary Update insurance policy
         * @param {string} clientId Client UUID
         * @param {string} policyId Insurance policy UUID
         * @param {UpdateClientInsurancePolicyRequest} updateClientInsurancePolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientInsurancePolicy(clientId: string, policyId: string, updateClientInsurancePolicyRequest: UpdateClientInsurancePolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientInsurancePolicy> {
            return localVarFp.updateClientInsurancePolicy(clientId, policyId, updateClientInsurancePolicyRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientFinancieringApi - object-oriented interface
 */
export class ClientFinancieringApi extends BaseAPI {
    /**
     * Creates a new insurance policy under the client\'s financiering record. Creates the financiering record on the fly if none exists yet. 
     * @summary Create insurance policy
     * @param {string} clientId Client UUID
     * @param {CreateClientInsurancePolicyRequest} createClientInsurancePolicyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createClientInsurancePolicy(clientId: string, createClientInsurancePolicyRequest: CreateClientInsurancePolicyRequest, options?: RawAxiosRequestConfig) {
        return ClientFinancieringApiFp(this.configuration).createClientInsurancePolicy(clientId, createClientInsurancePolicyRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes an insurance policy from the client\'s financiering record.
     * @summary Delete insurance policy
     * @param {string} clientId Client UUID
     * @param {string} policyId Insurance policy UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientInsurancePolicy(clientId: string, policyId: string, options?: RawAxiosRequestConfig) {
        return ClientFinancieringApiFp(this.configuration).deleteClientInsurancePolicy(clientId, policyId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves financing (financiering) data for a client including insurance information.
     * @summary Get client financing data
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientFinanciering(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientFinancieringApiFp(this.configuration).getClientFinanciering(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Verifies the client\'s insurance status with VECOZO COV using the client\'s BSN and date of birth, then upserts the returned policies on the client\'s financiering record.  Matching is by `(insurerId, verzekerdenummer)`: - Existing matched policies are updated from the COV response. - Unmatched COV results become new policies with `source=COV`. - Existing `source=COV` policies that are missing from the response are   deactivated (`isActive=false`); they are never deleted, so the   history is preserved. - `source=MANUAL` policies are not touched.  A COV coverage whose UZOVI code is not in the insurer catalogue cannot be stored and is reported in `skippedCoverages`. Such a run leaves the existing policies alone — nothing is deactivated — because an absent insurer is a gap in our reference data, not proof the client lost the coverage.  Every touched row gets `lastVerifiedAt` stamped to the current time. 
     * @summary Sync insurance policies from VECOZO COV
     * @param {string} clientId Client UUID
     * @param {string} [peildatum] Reference date for the COV check. Defaults to today.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public syncClientInsurancePoliciesFromCov(clientId: string, peildatum?: string, options?: RawAxiosRequestConfig) {
        return ClientFinancieringApiFp(this.configuration).syncClientInsurancePoliciesFromCov(clientId, peildatum, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates financing (financiering) data for a client.
     * @summary Update client financing data
     * @param {string} clientId Client UUID
     * @param {UpdateClientFinancieringRequest} updateClientFinancieringRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientFinanciering(clientId: string, updateClientFinancieringRequest: UpdateClientFinancieringRequest, options?: RawAxiosRequestConfig) {
        return ClientFinancieringApiFp(this.configuration).updateClientFinanciering(clientId, updateClientFinancieringRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates fields on an existing insurance policy. Only provided fields are changed.
     * @summary Update insurance policy
     * @param {string} clientId Client UUID
     * @param {string} policyId Insurance policy UUID
     * @param {UpdateClientInsurancePolicyRequest} updateClientInsurancePolicyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientInsurancePolicy(clientId: string, policyId: string, updateClientInsurancePolicyRequest: UpdateClientInsurancePolicyRequest, options?: RawAxiosRequestConfig) {
        return ClientFinancieringApiFp(this.configuration).updateClientInsurancePolicy(clientId, policyId, updateClientInsurancePolicyRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientFinancingsApi - axios parameter creator
 */
export const ClientFinancingsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Records a financing under one financing regime for a client. A client may hold several financings (over time, or for combined problematiek). 
         * @summary Create client financing
         * @param {string} clientId Client UUID
         * @param {CreateClientFinancingRequest} createClientFinancingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientFinancing: async (clientId: string, createClientFinancingRequest: CreateClientFinancingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('createClientFinancing', 'clientId', clientId)
            // verify required parameter 'createClientFinancingRequest' is not null or undefined
            assertParamExists('createClientFinancing', 'createClientFinancingRequest', createClientFinancingRequest)
            const localVarPath = `/api/v1/clients/{clientId}/financings`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createClientFinancingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes a financing from the client\'s dossier.
         * @summary Delete client financing
         * @param {string} clientId Client UUID
         * @param {string} financingId Financing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientFinancing: async (clientId: string, financingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClientFinancing', 'clientId', clientId)
            // verify required parameter 'financingId' is not null or undefined
            assertParamExists('deleteClientFinancing', 'financingId', financingId)
            const localVarPath = `/api/v1/clients/{clientId}/financings/{financingId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"financingId"}}`, encodeURIComponent(String(financingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists every financing (financiering per regime) recorded for a client, most recently created first. Both active and inactive financings are returned; `isActive` says which. 
         * @summary List client financings
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientFinancings: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientFinancings', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/financings`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates a client\'s financing. Every property is optional; an omitted property is left as stored, an explicit `null` clears it. 
         * @summary Update client financing
         * @param {string} clientId Client UUID
         * @param {string} financingId Financing UUID
         * @param {UpdateClientFinancingRequest} updateClientFinancingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientFinancing: async (clientId: string, financingId: string, updateClientFinancingRequest: UpdateClientFinancingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientFinancing', 'clientId', clientId)
            // verify required parameter 'financingId' is not null or undefined
            assertParamExists('updateClientFinancing', 'financingId', financingId)
            // verify required parameter 'updateClientFinancingRequest' is not null or undefined
            assertParamExists('updateClientFinancing', 'updateClientFinancingRequest', updateClientFinancingRequest)
            const localVarPath = `/api/v1/clients/{clientId}/financings/{financingId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"financingId"}}`, encodeURIComponent(String(financingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientFinancingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientFinancingsApi - functional programming interface
 */
export const ClientFinancingsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientFinancingsApiAxiosParamCreator(configuration)
    return {
        /**
         * Records a financing under one financing regime for a client. A client may hold several financings (over time, or for combined problematiek). 
         * @summary Create client financing
         * @param {string} clientId Client UUID
         * @param {CreateClientFinancingRequest} createClientFinancingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createClientFinancing(clientId: string, createClientFinancingRequest: CreateClientFinancingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFinancingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createClientFinancing(clientId, createClientFinancingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancingsApi.createClientFinancing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes a financing from the client\'s dossier.
         * @summary Delete client financing
         * @param {string} clientId Client UUID
         * @param {string} financingId Financing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientFinancing(clientId: string, financingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientFinancing(clientId, financingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancingsApi.deleteClientFinancing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists every financing (financiering per regime) recorded for a client, most recently created first. Both active and inactive financings are returned; `isActive` says which. 
         * @summary List client financings
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientFinancings(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ClientFinancingResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientFinancings(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancingsApi.listClientFinancings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates a client\'s financing. Every property is optional; an omitted property is left as stored, an explicit `null` clears it. 
         * @summary Update client financing
         * @param {string} clientId Client UUID
         * @param {string} financingId Financing UUID
         * @param {UpdateClientFinancingRequest} updateClientFinancingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientFinancing(clientId: string, financingId: string, updateClientFinancingRequest: UpdateClientFinancingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFinancingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientFinancing(clientId, financingId, updateClientFinancingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientFinancingsApi.updateClientFinancing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientFinancingsApi - factory interface
 */
export const ClientFinancingsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientFinancingsApiFp(configuration)
    return {
        /**
         * Records a financing under one financing regime for a client. A client may hold several financings (over time, or for combined problematiek). 
         * @summary Create client financing
         * @param {string} clientId Client UUID
         * @param {CreateClientFinancingRequest} createClientFinancingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientFinancing(clientId: string, createClientFinancingRequest: CreateClientFinancingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientFinancingResponse> {
            return localVarFp.createClientFinancing(clientId, createClientFinancingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes a financing from the client\'s dossier.
         * @summary Delete client financing
         * @param {string} clientId Client UUID
         * @param {string} financingId Financing UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientFinancing(clientId: string, financingId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientFinancing(clientId, financingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists every financing (financiering per regime) recorded for a client, most recently created first. Both active and inactive financings are returned; `isActive` says which. 
         * @summary List client financings
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientFinancings(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ClientFinancingResponse>> {
            return localVarFp.listClientFinancings(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates a client\'s financing. Every property is optional; an omitted property is left as stored, an explicit `null` clears it. 
         * @summary Update client financing
         * @param {string} clientId Client UUID
         * @param {string} financingId Financing UUID
         * @param {UpdateClientFinancingRequest} updateClientFinancingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientFinancing(clientId: string, financingId: string, updateClientFinancingRequest: UpdateClientFinancingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientFinancingResponse> {
            return localVarFp.updateClientFinancing(clientId, financingId, updateClientFinancingRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientFinancingsApi - object-oriented interface
 */
export class ClientFinancingsApi extends BaseAPI {
    /**
     * Records a financing under one financing regime for a client. A client may hold several financings (over time, or for combined problematiek). 
     * @summary Create client financing
     * @param {string} clientId Client UUID
     * @param {CreateClientFinancingRequest} createClientFinancingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createClientFinancing(clientId: string, createClientFinancingRequest: CreateClientFinancingRequest, options?: RawAxiosRequestConfig) {
        return ClientFinancingsApiFp(this.configuration).createClientFinancing(clientId, createClientFinancingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes a financing from the client\'s dossier.
     * @summary Delete client financing
     * @param {string} clientId Client UUID
     * @param {string} financingId Financing UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientFinancing(clientId: string, financingId: string, options?: RawAxiosRequestConfig) {
        return ClientFinancingsApiFp(this.configuration).deleteClientFinancing(clientId, financingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists every financing (financiering per regime) recorded for a client, most recently created first. Both active and inactive financings are returned; `isActive` says which. 
     * @summary List client financings
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientFinancings(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientFinancingsApiFp(this.configuration).listClientFinancings(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates a client\'s financing. Every property is optional; an omitted property is left as stored, an explicit `null` clears it. 
     * @summary Update client financing
     * @param {string} clientId Client UUID
     * @param {string} financingId Financing UUID
     * @param {UpdateClientFinancingRequest} updateClientFinancingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientFinancing(clientId: string, financingId: string, updateClientFinancingRequest: UpdateClientFinancingRequest, options?: RawAxiosRequestConfig) {
        return ClientFinancingsApiFp(this.configuration).updateClientFinancing(clientId, financingId, updateClientFinancingRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientVerantwoordelijkeGemeenteApi - axios parameter creator
 */
export const ClientVerantwoordelijkeGemeenteApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * A person confirms the gemeente responsible for the cliënt under one regime from `geldigVanaf` on. The current period of that regime ends the day before; a period may not start on or before a later one already confirmed.  `bron` is derived, never sent: `TOEWIJZING` when `ijwBerichtId` names a JW301 received about this cliënt from `gemeentecode`, `SUGGESTIE` when the cliënt\'s woonplaats suggests exactly this code, `HANDMATIG` otherwise.  A `400` carries an `errors` array naming each field: a grondslag of the other regime, a gemeentecode that names no gemeente on `geldigVanaf`, an overlapping period, or a bericht that is not this cliënt\'s JW301 from this gemeente. 
         * @summary Confirm the cliënt\'s verantwoordelijke gemeente
         * @param {string} clientId Client UUID
         * @param {CreateVerantwoordelijkeGemeenteRequest} createVerantwoordelijkeGemeenteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createVerantwoordelijkeGemeente: async (clientId: string, createVerantwoordelijkeGemeenteRequest: CreateVerantwoordelijkeGemeenteRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('createVerantwoordelijkeGemeente', 'clientId', clientId)
            // verify required parameter 'createVerantwoordelijkeGemeenteRequest' is not null or undefined
            assertParamExists('createVerantwoordelijkeGemeente', 'createVerantwoordelijkeGemeenteRequest', createVerantwoordelijkeGemeenteRequest)
            const localVarPath = `/api/v1/clients/{clientId}/verantwoordelijke-gemeenten`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createVerantwoordelijkeGemeenteRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every confirmed period, per regime, latest first. The period with no `geldigTot` is the current one. 
         * @summary List the cliënt\'s verantwoordelijke gemeenten
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listVerantwoordelijkeGemeenten: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listVerantwoordelijkeGemeenten', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/verantwoordelijke-gemeenten`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * What the cliënt\'s woonplaats (`addressCity`) suggests today, from the CBS woonplaatsen and nothing else — no address leaves Scrivio. One code, several candidates when the name fits woonplaatsen in more than one gemeente, or none. A suggestion only: a person confirms it. 
         * @summary Suggest the cliënt\'s verantwoordelijke gemeente
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestVerantwoordelijkeGemeente: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('suggestVerantwoordelijkeGemeente', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/verantwoordelijke-gemeenten/suggestie`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientVerantwoordelijkeGemeenteApi - functional programming interface
 */
export const ClientVerantwoordelijkeGemeenteApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientVerantwoordelijkeGemeenteApiAxiosParamCreator(configuration)
    return {
        /**
         * A person confirms the gemeente responsible for the cliënt under one regime from `geldigVanaf` on. The current period of that regime ends the day before; a period may not start on or before a later one already confirmed.  `bron` is derived, never sent: `TOEWIJZING` when `ijwBerichtId` names a JW301 received about this cliënt from `gemeentecode`, `SUGGESTIE` when the cliënt\'s woonplaats suggests exactly this code, `HANDMATIG` otherwise.  A `400` carries an `errors` array naming each field: a grondslag of the other regime, a gemeentecode that names no gemeente on `geldigVanaf`, an overlapping period, or a bericht that is not this cliënt\'s JW301 from this gemeente. 
         * @summary Confirm the cliënt\'s verantwoordelijke gemeente
         * @param {string} clientId Client UUID
         * @param {CreateVerantwoordelijkeGemeenteRequest} createVerantwoordelijkeGemeenteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createVerantwoordelijkeGemeente(clientId: string, createVerantwoordelijkeGemeenteRequest: CreateVerantwoordelijkeGemeenteRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VerantwoordelijkeGemeenteResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createVerantwoordelijkeGemeente(clientId, createVerantwoordelijkeGemeenteRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientVerantwoordelijkeGemeenteApi.createVerantwoordelijkeGemeente']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every confirmed period, per regime, latest first. The period with no `geldigTot` is the current one. 
         * @summary List the cliënt\'s verantwoordelijke gemeenten
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listVerantwoordelijkeGemeenten(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<VerantwoordelijkeGemeenteResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listVerantwoordelijkeGemeenten(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientVerantwoordelijkeGemeenteApi.listVerantwoordelijkeGemeenten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * What the cliënt\'s woonplaats (`addressCity`) suggests today, from the CBS woonplaatsen and nothing else — no address leaves Scrivio. One code, several candidates when the name fits woonplaatsen in more than one gemeente, or none. A suggestion only: a person confirms it. 
         * @summary Suggest the cliënt\'s verantwoordelijke gemeente
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async suggestVerantwoordelijkeGemeente(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VerantwoordelijkeGemeenteSuggestieResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.suggestVerantwoordelijkeGemeente(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientVerantwoordelijkeGemeenteApi.suggestVerantwoordelijkeGemeente']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientVerantwoordelijkeGemeenteApi - factory interface
 */
export const ClientVerantwoordelijkeGemeenteApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientVerantwoordelijkeGemeenteApiFp(configuration)
    return {
        /**
         * A person confirms the gemeente responsible for the cliënt under one regime from `geldigVanaf` on. The current period of that regime ends the day before; a period may not start on or before a later one already confirmed.  `bron` is derived, never sent: `TOEWIJZING` when `ijwBerichtId` names a JW301 received about this cliënt from `gemeentecode`, `SUGGESTIE` when the cliënt\'s woonplaats suggests exactly this code, `HANDMATIG` otherwise.  A `400` carries an `errors` array naming each field: a grondslag of the other regime, a gemeentecode that names no gemeente on `geldigVanaf`, an overlapping period, or a bericht that is not this cliënt\'s JW301 from this gemeente. 
         * @summary Confirm the cliënt\'s verantwoordelijke gemeente
         * @param {string} clientId Client UUID
         * @param {CreateVerantwoordelijkeGemeenteRequest} createVerantwoordelijkeGemeenteRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createVerantwoordelijkeGemeente(clientId: string, createVerantwoordelijkeGemeenteRequest: CreateVerantwoordelijkeGemeenteRequest, options?: RawAxiosRequestConfig): AxiosPromise<VerantwoordelijkeGemeenteResponse> {
            return localVarFp.createVerantwoordelijkeGemeente(clientId, createVerantwoordelijkeGemeenteRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Every confirmed period, per regime, latest first. The period with no `geldigTot` is the current one. 
         * @summary List the cliënt\'s verantwoordelijke gemeenten
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listVerantwoordelijkeGemeenten(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<VerantwoordelijkeGemeenteResponse>> {
            return localVarFp.listVerantwoordelijkeGemeenten(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * What the cliënt\'s woonplaats (`addressCity`) suggests today, from the CBS woonplaatsen and nothing else — no address leaves Scrivio. One code, several candidates when the name fits woonplaatsen in more than one gemeente, or none. A suggestion only: a person confirms it. 
         * @summary Suggest the cliënt\'s verantwoordelijke gemeente
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        suggestVerantwoordelijkeGemeente(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<VerantwoordelijkeGemeenteSuggestieResponse> {
            return localVarFp.suggestVerantwoordelijkeGemeente(clientId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientVerantwoordelijkeGemeenteApi - object-oriented interface
 */
export class ClientVerantwoordelijkeGemeenteApi extends BaseAPI {
    /**
     * A person confirms the gemeente responsible for the cliënt under one regime from `geldigVanaf` on. The current period of that regime ends the day before; a period may not start on or before a later one already confirmed.  `bron` is derived, never sent: `TOEWIJZING` when `ijwBerichtId` names a JW301 received about this cliënt from `gemeentecode`, `SUGGESTIE` when the cliënt\'s woonplaats suggests exactly this code, `HANDMATIG` otherwise.  A `400` carries an `errors` array naming each field: a grondslag of the other regime, a gemeentecode that names no gemeente on `geldigVanaf`, an overlapping period, or a bericht that is not this cliënt\'s JW301 from this gemeente. 
     * @summary Confirm the cliënt\'s verantwoordelijke gemeente
     * @param {string} clientId Client UUID
     * @param {CreateVerantwoordelijkeGemeenteRequest} createVerantwoordelijkeGemeenteRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createVerantwoordelijkeGemeente(clientId: string, createVerantwoordelijkeGemeenteRequest: CreateVerantwoordelijkeGemeenteRequest, options?: RawAxiosRequestConfig) {
        return ClientVerantwoordelijkeGemeenteApiFp(this.configuration).createVerantwoordelijkeGemeente(clientId, createVerantwoordelijkeGemeenteRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every confirmed period, per regime, latest first. The period with no `geldigTot` is the current one. 
     * @summary List the cliënt\'s verantwoordelijke gemeenten
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listVerantwoordelijkeGemeenten(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientVerantwoordelijkeGemeenteApiFp(this.configuration).listVerantwoordelijkeGemeenten(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * What the cliënt\'s woonplaats (`addressCity`) suggests today, from the CBS woonplaatsen and nothing else — no address leaves Scrivio. One code, several candidates when the name fits woonplaatsen in more than one gemeente, or none. A suggestion only: a person confirms it. 
     * @summary Suggest the cliënt\'s verantwoordelijke gemeente
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public suggestVerantwoordelijkeGemeente(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientVerantwoordelijkeGemeenteApiFp(this.configuration).suggestVerantwoordelijkeGemeente(clientId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientZorgvraagApi - axios parameter creator
 */
export const ClientZorgvraagApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Retrieves the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
         * @summary Get client care needs and offer
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientZorgvraag: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientZorgvraag', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/zorgvraag`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
         * @summary Update client care needs and offer
         * @param {string} clientId Client UUID
         * @param {UpdateClientZorgvraagRequest} updateClientZorgvraagRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientZorgvraag: async (clientId: string, updateClientZorgvraagRequest: UpdateClientZorgvraagRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientZorgvraag', 'clientId', clientId)
            // verify required parameter 'updateClientZorgvraagRequest' is not null or undefined
            assertParamExists('updateClientZorgvraag', 'updateClientZorgvraagRequest', updateClientZorgvraagRequest)
            const localVarPath = `/api/v1/clients/{clientId}/zorgvraag`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientZorgvraagRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientZorgvraagApi - functional programming interface
 */
export const ClientZorgvraagApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientZorgvraagApiAxiosParamCreator(configuration)
    return {
        /**
         * Retrieves the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
         * @summary Get client care needs and offer
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientZorgvraag(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientZorgvraagResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientZorgvraag(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientZorgvraagApi.getClientZorgvraag']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
         * @summary Update client care needs and offer
         * @param {string} clientId Client UUID
         * @param {UpdateClientZorgvraagRequest} updateClientZorgvraagRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientZorgvraag(clientId: string, updateClientZorgvraagRequest: UpdateClientZorgvraagRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientZorgvraagResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientZorgvraag(clientId, updateClientZorgvraagRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientZorgvraagApi.updateClientZorgvraag']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientZorgvraagApi - factory interface
 */
export const ClientZorgvraagApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientZorgvraagApiFp(configuration)
    return {
        /**
         * Retrieves the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
         * @summary Get client care needs and offer
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientZorgvraag(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientZorgvraagResponse> {
            return localVarFp.getClientZorgvraag(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
         * @summary Update client care needs and offer
         * @param {string} clientId Client UUID
         * @param {UpdateClientZorgvraagRequest} updateClientZorgvraagRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientZorgvraag(clientId: string, updateClientZorgvraagRequest: UpdateClientZorgvraagRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientZorgvraagResponse> {
            return localVarFp.updateClientZorgvraag(clientId, updateClientZorgvraagRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientZorgvraagApi - object-oriented interface
 */
export class ClientZorgvraagApi extends BaseAPI {
    /**
     * Retrieves the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
     * @summary Get client care needs and offer
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientZorgvraag(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientZorgvraagApiFp(this.configuration).getClientZorgvraag(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the care needs (zorgvraag) and care offer (zorgaanbod) for a client.
     * @summary Update client care needs and offer
     * @param {string} clientId Client UUID
     * @param {UpdateClientZorgvraagRequest} updateClientZorgvraagRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientZorgvraag(clientId: string, updateClientZorgvraagRequest: UpdateClientZorgvraagRequest, options?: RawAxiosRequestConfig) {
        return ClientZorgvraagApiFp(this.configuration).updateClientZorgvraag(clientId, updateClientZorgvraagRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientsApi - axios parameter creator
 */
export const ClientsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Archives a client by setting archivedAt timestamp. Archived clients are hidden by default but can still be accessed. 
         * @summary Archive client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveClient: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('archiveClient', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/archive`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Assigns a behandelaar (user) to a client\'s treatment team.
         * @summary Assign behandelaar to client
         * @param {string} clientId Client UUID
         * @param {string} userId User UUID to assign as behandelaar
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignBehandelaar: async (clientId: string, userId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('assignBehandelaar', 'clientId', clientId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('assignBehandelaar', 'userId', userId)
            const localVarPath = `/api/v1/clients/{clientId}/behandelaars/{userId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new client in the authenticated user\'s organization
         * @summary Create new client
         * @param {CreateClientRequest} createClientRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClient: async (createClientRequest: CreateClientRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createClientRequest' is not null or undefined
            assertParamExists('createClient', 'createClientRequest', createClientRequest)
            const localVarPath = `/api/v1/clients`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createClientRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft deletes a client by setting deletedAt timestamp. Client will no longer appear in lists or be accessible. 
         * @summary Soft delete client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClient: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClient', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns authorization capabilities for the authenticated user on multiple clients. Used by list views to determine which actions are available for each client row without making N+1 API calls.  - Maximum 50 client IDs per request - Clients the user cannot view will return `{ \"view\": false }` only - Clients from other organizations or non-existent clients also return `{ \"view\": false }` 
         * @summary Get capabilities for multiple clients
         * @param {BulkCapabilitiesRequest} bulkCapabilitiesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBulkClientCapabilities: async (bulkCapabilitiesRequest: BulkCapabilitiesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'bulkCapabilitiesRequest' is not null or undefined
            assertParamExists('getBulkClientCapabilities', 'bulkCapabilitiesRequest', bulkCapabilitiesRequest)
            const localVarPath = `/api/v1/clients/capabilities`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkCapabilitiesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves all active behandelaar assignments for a client.
         * @summary Get assigned behandelaars
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientBehandelaars: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientBehandelaars', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/behandelaars`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a single client by ID. User must be in same organization.
         * @summary Get client by ID
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientById: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientById', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns authorization capabilities for the authenticated user on a specific client. Used by the frontend to determine which actions are available for the current user. 
         * @summary Get client capabilities
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientCapabilities: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientCapabilities', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/capabilities`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List clients in the authenticated user\'s organization with pagination, search, and filtering capabilities. 
         * @summary List clients
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: createdAt, updatedAt, firstName, lastName, email, dateOfBirth, intakeDate, patientNumber. Any other field falls back to lastName.
         * @param {string} [search] The cliënt search — the same search as the app\&#39;s global cliënt search field. It matches the name (per word, so \&quot;Floor de Wit\&quot; finds a cliënt with tussenvoegsel \&quot;de\&quot;), e-mail, BSN, address, external reference and patiëntnummer as a case-insensitive substring, so \&quot;123\&quot; finds patiëntnummer \&quot;P-000123\&quot;. A date of birth matches written as yyyy-mm-dd (also in part, e.g. \&quot;1985-03\&quot;) or as a full Dutch date dd-mm-yyyy (also \&quot;12/03/1985\&quot;, \&quot;12.03.1985\&quot; or \&quot;12031985\&quot;).
         * @param {string} [locationId] Filter by location ID
         * @param {boolean} [archived] Include archived clients (default false shows only active)
         * @param {string} [clientStateId] Filter by current client state (status). Returns only clients whose most recent state period matches this state.
         * @param {Array<string>} [clientIds] Restrict the page to exactly these clients — the overview narrowed to a set somebody else already worked out, rather than a property of the clients themselves.  Combined with the other filters rather than replacing them, so the archived, location and status filters still apply on top. Omitted or empty means no restriction; an id outside the reader\&#39;s visibility scope simply does not come back. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClients: async (page?: number, size?: number, sort?: string, search?: string, locationId?: string, archived?: boolean, clientStateId?: string, clientIds?: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/clients`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (locationId !== undefined) {
                localVarQueryParameter['locationId'] = locationId;
            }

            if (archived !== undefined) {
                localVarQueryParameter['archived'] = archived;
            }

            if (clientStateId !== undefined) {
                localVarQueryParameter['clientStateId'] = clientStateId;
            }

            if (clientIds) {
                localVarQueryParameter['clientIds'] = clientIds;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sends the cliënt one mail at the dossier\'s e-mailadres saying that the practice uses a cliëntportaal, with a button to it and how signing in works (#4718). Never sent automatically: the create form can send it at the user\'s explicit request, and an import never does. The mail names the organisation, is signed with its name, and carries its e-mailadres as Reply-To when one is set; it holds no cliënt data beyond the greeting.  Stamps `portalInvitedAt` and records a `SEND` audit event. Sending again is allowed and overwrites the timestamp.  Authorized like editing the cliënt: `clients.update`, or `clients.update.own` on a cliënt whose behandelteam the user is on. `ClientCapabilities.inviteToPortal` predicts it. 
         * @summary Invite the client to the cliëntportaal
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendClientPortalInvitation: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('sendClientPortalInvitation', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/portal-invitation`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Unarchives a client by clearing archivedAt timestamp.
         * @summary Unarchive client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveClient: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('unarchiveClient', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/unarchive`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes a behandelaar assignment by setting unassignedAt timestamp.
         * @summary Unassign behandelaar from client
         * @param {string} clientId Client UUID
         * @param {string} userId User UUID to unassign
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignBehandelaar: async (clientId: string, userId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('unassignBehandelaar', 'clientId', clientId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('unassignBehandelaar', 'userId', userId)
            const localVarPath = `/api/v1/clients/{clientId}/behandelaars/{userId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing client. User must be in same organization.
         * @summary Update client
         * @param {string} clientId Client UUID
         * @param {UpdateClientRequest} updateClientRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClient: async (clientId: string, updateClientRequest: UpdateClientRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClient', 'clientId', clientId)
            // verify required parameter 'updateClientRequest' is not null or undefined
            assertParamExists('updateClient', 'updateClientRequest', updateClientRequest)
            const localVarPath = `/api/v1/clients/{clientId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientsApi - functional programming interface
 */
export const ClientsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientsApiAxiosParamCreator(configuration)
    return {
        /**
         * Archives a client by setting archivedAt timestamp. Archived clients are hidden by default but can still be accessed. 
         * @summary Archive client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveClient(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveClient(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.archiveClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Assigns a behandelaar (user) to a client\'s treatment team.
         * @summary Assign behandelaar to client
         * @param {string} clientId Client UUID
         * @param {string} userId User UUID to assign as behandelaar
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assignBehandelaar(clientId: string, userId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientBehandelaarResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assignBehandelaar(clientId, userId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.assignBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new client in the authenticated user\'s organization
         * @summary Create new client
         * @param {CreateClientRequest} createClientRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createClient(createClientRequest: CreateClientRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createClient(createClientRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.createClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft deletes a client by setting deletedAt timestamp. Client will no longer appear in lists or be accessible. 
         * @summary Soft delete client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClient(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClient(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.deleteClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns authorization capabilities for the authenticated user on multiple clients. Used by list views to determine which actions are available for each client row without making N+1 API calls.  - Maximum 50 client IDs per request - Clients the user cannot view will return `{ \"view\": false }` only - Clients from other organizations or non-existent clients also return `{ \"view\": false }` 
         * @summary Get capabilities for multiple clients
         * @param {BulkCapabilitiesRequest} bulkCapabilitiesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBulkClientCapabilities(bulkCapabilitiesRequest: BulkCapabilitiesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<{ [key: string]: ClientCapabilities; }>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBulkClientCapabilities(bulkCapabilitiesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.getBulkClientCapabilities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves all active behandelaar assignments for a client.
         * @summary Get assigned behandelaars
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientBehandelaars(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ClientBehandelaarResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientBehandelaars(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.getClientBehandelaars']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a single client by ID. User must be in same organization.
         * @summary Get client by ID
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientById(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientById(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.getClientById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns authorization capabilities for the authenticated user on a specific client. Used by the frontend to determine which actions are available for the current user. 
         * @summary Get client capabilities
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientCapabilities(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientCapabilities>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientCapabilities(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.getClientCapabilities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List clients in the authenticated user\'s organization with pagination, search, and filtering capabilities. 
         * @summary List clients
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: createdAt, updatedAt, firstName, lastName, email, dateOfBirth, intakeDate, patientNumber. Any other field falls back to lastName.
         * @param {string} [search] The cliënt search — the same search as the app\&#39;s global cliënt search field. It matches the name (per word, so \&quot;Floor de Wit\&quot; finds a cliënt with tussenvoegsel \&quot;de\&quot;), e-mail, BSN, address, external reference and patiëntnummer as a case-insensitive substring, so \&quot;123\&quot; finds patiëntnummer \&quot;P-000123\&quot;. A date of birth matches written as yyyy-mm-dd (also in part, e.g. \&quot;1985-03\&quot;) or as a full Dutch date dd-mm-yyyy (also \&quot;12/03/1985\&quot;, \&quot;12.03.1985\&quot; or \&quot;12031985\&quot;).
         * @param {string} [locationId] Filter by location ID
         * @param {boolean} [archived] Include archived clients (default false shows only active)
         * @param {string} [clientStateId] Filter by current client state (status). Returns only clients whose most recent state period matches this state.
         * @param {Array<string>} [clientIds] Restrict the page to exactly these clients — the overview narrowed to a set somebody else already worked out, rather than a property of the clients themselves.  Combined with the other filters rather than replacing them, so the archived, location and status filters still apply on top. Omitted or empty means no restriction; an id outside the reader\&#39;s visibility scope simply does not come back. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClients(page?: number, size?: number, sort?: string, search?: string, locationId?: string, archived?: boolean, clientStateId?: string, clientIds?: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClients(page, size, sort, search, locationId, archived, clientStateId, clientIds, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.listClients']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sends the cliënt one mail at the dossier\'s e-mailadres saying that the practice uses a cliëntportaal, with a button to it and how signing in works (#4718). Never sent automatically: the create form can send it at the user\'s explicit request, and an import never does. The mail names the organisation, is signed with its name, and carries its e-mailadres as Reply-To when one is set; it holds no cliënt data beyond the greeting.  Stamps `portalInvitedAt` and records a `SEND` audit event. Sending again is allowed and overwrites the timestamp.  Authorized like editing the cliënt: `clients.update`, or `clients.update.own` on a cliënt whose behandelteam the user is on. `ClientCapabilities.inviteToPortal` predicts it. 
         * @summary Invite the client to the cliëntportaal
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendClientPortalInvitation(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendClientPortalInvitation(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.sendClientPortalInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Unarchives a client by clearing archivedAt timestamp.
         * @summary Unarchive client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unarchiveClient(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unarchiveClient(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.unarchiveClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes a behandelaar assignment by setting unassignedAt timestamp.
         * @summary Unassign behandelaar from client
         * @param {string} clientId Client UUID
         * @param {string} userId User UUID to unassign
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unassignBehandelaar(clientId: string, userId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unassignBehandelaar(clientId, userId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.unassignBehandelaar']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing client. User must be in same organization.
         * @summary Update client
         * @param {string} clientId Client UUID
         * @param {UpdateClientRequest} updateClientRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClient(clientId: string, updateClientRequest: UpdateClientRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClient(clientId, updateClientRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientsApi.updateClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientsApi - factory interface
 */
export const ClientsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientsApiFp(configuration)
    return {
        /**
         * Archives a client by setting archivedAt timestamp. Archived clients are hidden by default but can still be accessed. 
         * @summary Archive client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveClient(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientProfile> {
            return localVarFp.archiveClient(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Assigns a behandelaar (user) to a client\'s treatment team.
         * @summary Assign behandelaar to client
         * @param {string} clientId Client UUID
         * @param {string} userId User UUID to assign as behandelaar
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignBehandelaar(clientId: string, userId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientBehandelaarResponse> {
            return localVarFp.assignBehandelaar(clientId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new client in the authenticated user\'s organization
         * @summary Create new client
         * @param {CreateClientRequest} createClientRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClient(createClientRequest: CreateClientRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientProfile> {
            return localVarFp.createClient(createClientRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft deletes a client by setting deletedAt timestamp. Client will no longer appear in lists or be accessible. 
         * @summary Soft delete client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClient(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClient(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns authorization capabilities for the authenticated user on multiple clients. Used by list views to determine which actions are available for each client row without making N+1 API calls.  - Maximum 50 client IDs per request - Clients the user cannot view will return `{ \"view\": false }` only - Clients from other organizations or non-existent clients also return `{ \"view\": false }` 
         * @summary Get capabilities for multiple clients
         * @param {BulkCapabilitiesRequest} bulkCapabilitiesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBulkClientCapabilities(bulkCapabilitiesRequest: BulkCapabilitiesRequest, options?: RawAxiosRequestConfig): AxiosPromise<{ [key: string]: ClientCapabilities; }> {
            return localVarFp.getBulkClientCapabilities(bulkCapabilitiesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves all active behandelaar assignments for a client.
         * @summary Get assigned behandelaars
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientBehandelaars(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ClientBehandelaarResponse>> {
            return localVarFp.getClientBehandelaars(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a single client by ID. User must be in same organization.
         * @summary Get client by ID
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientById(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientProfile> {
            return localVarFp.getClientById(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns authorization capabilities for the authenticated user on a specific client. Used by the frontend to determine which actions are available for the current user. 
         * @summary Get client capabilities
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientCapabilities(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientCapabilities> {
            return localVarFp.getClientCapabilities(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * List clients in the authenticated user\'s organization with pagination, search, and filtering capabilities. 
         * @summary List clients
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: createdAt, updatedAt, firstName, lastName, email, dateOfBirth, intakeDate, patientNumber. Any other field falls back to lastName.
         * @param {string} [search] The cliënt search — the same search as the app\&#39;s global cliënt search field. It matches the name (per word, so \&quot;Floor de Wit\&quot; finds a cliënt with tussenvoegsel \&quot;de\&quot;), e-mail, BSN, address, external reference and patiëntnummer as a case-insensitive substring, so \&quot;123\&quot; finds patiëntnummer \&quot;P-000123\&quot;. A date of birth matches written as yyyy-mm-dd (also in part, e.g. \&quot;1985-03\&quot;) or as a full Dutch date dd-mm-yyyy (also \&quot;12/03/1985\&quot;, \&quot;12.03.1985\&quot; or \&quot;12031985\&quot;).
         * @param {string} [locationId] Filter by location ID
         * @param {boolean} [archived] Include archived clients (default false shows only active)
         * @param {string} [clientStateId] Filter by current client state (status). Returns only clients whose most recent state period matches this state.
         * @param {Array<string>} [clientIds] Restrict the page to exactly these clients — the overview narrowed to a set somebody else already worked out, rather than a property of the clients themselves.  Combined with the other filters rather than replacing them, so the archived, location and status filters still apply on top. Omitted or empty means no restriction; an id outside the reader\&#39;s visibility scope simply does not come back. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClients(page?: number, size?: number, sort?: string, search?: string, locationId?: string, archived?: boolean, clientStateId?: string, clientIds?: Array<string>, options?: RawAxiosRequestConfig): AxiosPromise<ClientListResponse> {
            return localVarFp.listClients(page, size, sort, search, locationId, archived, clientStateId, clientIds, options).then((request) => request(axios, basePath));
        },
        /**
         * Sends the cliënt one mail at the dossier\'s e-mailadres saying that the practice uses a cliëntportaal, with a button to it and how signing in works (#4718). Never sent automatically: the create form can send it at the user\'s explicit request, and an import never does. The mail names the organisation, is signed with its name, and carries its e-mailadres as Reply-To when one is set; it holds no cliënt data beyond the greeting.  Stamps `portalInvitedAt` and records a `SEND` audit event. Sending again is allowed and overwrites the timestamp.  Authorized like editing the cliënt: `clients.update`, or `clients.update.own` on a cliënt whose behandelteam the user is on. `ClientCapabilities.inviteToPortal` predicts it. 
         * @summary Invite the client to the cliëntportaal
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendClientPortalInvitation(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientProfile> {
            return localVarFp.sendClientPortalInvitation(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Unarchives a client by clearing archivedAt timestamp.
         * @summary Unarchive client
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveClient(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientProfile> {
            return localVarFp.unarchiveClient(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes a behandelaar assignment by setting unassignedAt timestamp.
         * @summary Unassign behandelaar from client
         * @param {string} clientId Client UUID
         * @param {string} userId User UUID to unassign
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignBehandelaar(clientId: string, userId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.unassignBehandelaar(clientId, userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing client. User must be in same organization.
         * @summary Update client
         * @param {string} clientId Client UUID
         * @param {UpdateClientRequest} updateClientRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClient(clientId: string, updateClientRequest: UpdateClientRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientProfile> {
            return localVarFp.updateClient(clientId, updateClientRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientsApi - object-oriented interface
 */
export class ClientsApi extends BaseAPI {
    /**
     * Archives a client by setting archivedAt timestamp. Archived clients are hidden by default but can still be accessed. 
     * @summary Archive client
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveClient(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).archiveClient(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Assigns a behandelaar (user) to a client\'s treatment team.
     * @summary Assign behandelaar to client
     * @param {string} clientId Client UUID
     * @param {string} userId User UUID to assign as behandelaar
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public assignBehandelaar(clientId: string, userId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).assignBehandelaar(clientId, userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new client in the authenticated user\'s organization
     * @summary Create new client
     * @param {CreateClientRequest} createClientRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createClient(createClientRequest: CreateClientRequest, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).createClient(createClientRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft deletes a client by setting deletedAt timestamp. Client will no longer appear in lists or be accessible. 
     * @summary Soft delete client
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClient(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).deleteClient(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns authorization capabilities for the authenticated user on multiple clients. Used by list views to determine which actions are available for each client row without making N+1 API calls.  - Maximum 50 client IDs per request - Clients the user cannot view will return `{ \"view\": false }` only - Clients from other organizations or non-existent clients also return `{ \"view\": false }` 
     * @summary Get capabilities for multiple clients
     * @param {BulkCapabilitiesRequest} bulkCapabilitiesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBulkClientCapabilities(bulkCapabilitiesRequest: BulkCapabilitiesRequest, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).getBulkClientCapabilities(bulkCapabilitiesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves all active behandelaar assignments for a client.
     * @summary Get assigned behandelaars
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientBehandelaars(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).getClientBehandelaars(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a single client by ID. User must be in same organization.
     * @summary Get client by ID
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientById(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).getClientById(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns authorization capabilities for the authenticated user on a specific client. Used by the frontend to determine which actions are available for the current user. 
     * @summary Get client capabilities
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientCapabilities(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).getClientCapabilities(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List clients in the authenticated user\'s organization with pagination, search, and filtering capabilities. 
     * @summary List clients
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: createdAt, updatedAt, firstName, lastName, email, dateOfBirth, intakeDate, patientNumber. Any other field falls back to lastName.
     * @param {string} [search] The cliënt search — the same search as the app\&#39;s global cliënt search field. It matches the name (per word, so \&quot;Floor de Wit\&quot; finds a cliënt with tussenvoegsel \&quot;de\&quot;), e-mail, BSN, address, external reference and patiëntnummer as a case-insensitive substring, so \&quot;123\&quot; finds patiëntnummer \&quot;P-000123\&quot;. A date of birth matches written as yyyy-mm-dd (also in part, e.g. \&quot;1985-03\&quot;) or as a full Dutch date dd-mm-yyyy (also \&quot;12/03/1985\&quot;, \&quot;12.03.1985\&quot; or \&quot;12031985\&quot;).
     * @param {string} [locationId] Filter by location ID
     * @param {boolean} [archived] Include archived clients (default false shows only active)
     * @param {string} [clientStateId] Filter by current client state (status). Returns only clients whose most recent state period matches this state.
     * @param {Array<string>} [clientIds] Restrict the page to exactly these clients — the overview narrowed to a set somebody else already worked out, rather than a property of the clients themselves.  Combined with the other filters rather than replacing them, so the archived, location and status filters still apply on top. Omitted or empty means no restriction; an id outside the reader\&#39;s visibility scope simply does not come back. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClients(page?: number, size?: number, sort?: string, search?: string, locationId?: string, archived?: boolean, clientStateId?: string, clientIds?: Array<string>, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).listClients(page, size, sort, search, locationId, archived, clientStateId, clientIds, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sends the cliënt one mail at the dossier\'s e-mailadres saying that the practice uses a cliëntportaal, with a button to it and how signing in works (#4718). Never sent automatically: the create form can send it at the user\'s explicit request, and an import never does. The mail names the organisation, is signed with its name, and carries its e-mailadres as Reply-To when one is set; it holds no cliënt data beyond the greeting.  Stamps `portalInvitedAt` and records a `SEND` audit event. Sending again is allowed and overwrites the timestamp.  Authorized like editing the cliënt: `clients.update`, or `clients.update.own` on a cliënt whose behandelteam the user is on. `ClientCapabilities.inviteToPortal` predicts it. 
     * @summary Invite the client to the cliëntportaal
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendClientPortalInvitation(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).sendClientPortalInvitation(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Unarchives a client by clearing archivedAt timestamp.
     * @summary Unarchive client
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unarchiveClient(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).unarchiveClient(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes a behandelaar assignment by setting unassignedAt timestamp.
     * @summary Unassign behandelaar from client
     * @param {string} clientId Client UUID
     * @param {string} userId User UUID to unassign
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unassignBehandelaar(clientId: string, userId: string, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).unassignBehandelaar(clientId, userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing client. User must be in same organization.
     * @summary Update client
     * @param {string} clientId Client UUID
     * @param {UpdateClientRequest} updateClientRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClient(clientId: string, updateClientRequest: UpdateClientRequest, options?: RawAxiosRequestConfig) {
        return ClientsApiFp(this.configuration).updateClient(clientId, updateClientRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FormAssignmentsApi - axios parameter creator
 */
export const FormAssignmentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Assigns a form to a client for them to fill in via the portal. Only active forms can be assigned. 
         * @summary Assign form to client
         * @param {string} clientId Client UUID
         * @param {AssignFormRequest} assignFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignFormToClient: async (clientId: string, assignFormRequest: AssignFormRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('assignFormToClient', 'clientId', clientId)
            // verify required parameter 'assignFormRequest' is not null or undefined
            assertParamExists('assignFormToClient', 'assignFormRequest', assignFormRequest)
            const localVarPath = `/api/v1/clients/{clientId}/form-assignments`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(assignFormRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Unassigns a form from a client, for example when it was assigned by mistake.  Only pending assignments can be removed. Once the client has completed the form the assignment is the record of that submission and removal is rejected with 409. 
         * @summary Remove a form assignment from a client
         * @param {string} clientId Client UUID
         * @param {string} assignmentId Form assignment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientFormAssignment: async (clientId: string, assignmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClientFormAssignment', 'clientId', clientId)
            // verify required parameter 'assignmentId' is not null or undefined
            assertParamExists('deleteClientFormAssignment', 'assignmentId', assignmentId)
            const localVarPath = `/api/v1/clients/{clientId}/form-assignments/{assignmentId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"assignmentId"}}`, encodeURIComponent(String(assignmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all form assignments for a specific client. Can be filtered by status (PENDING, COMPLETED). 
         * @summary List client\'s form assignments
         * @param {string} clientId Client UUID
         * @param {ListClientFormAssignmentsStatusEnum} [status] Filter by assignment status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientFormAssignments: async (clientId: string, status?: ListClientFormAssignmentsStatusEnum, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientFormAssignments', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/form-assignments`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Controls whether an assigned form is visible in the client portal.  Assigning a form in the dossier does not publish it to the client: portal visibility is opt-in and defaults to hidden. Hiding a form again does not delete answers the client already submitted — it only removes the form from the portal. 
         * @summary Share a form assignment with the client portal, or hide it
         * @param {string} clientId Client UUID
         * @param {string} assignmentId Form assignment UUID
         * @param {UpdateFormAssignmentSharingRequest} updateFormAssignmentSharingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFormAssignmentSharing: async (clientId: string, assignmentId: string, updateFormAssignmentSharingRequest: UpdateFormAssignmentSharingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateFormAssignmentSharing', 'clientId', clientId)
            // verify required parameter 'assignmentId' is not null or undefined
            assertParamExists('updateFormAssignmentSharing', 'assignmentId', assignmentId)
            // verify required parameter 'updateFormAssignmentSharingRequest' is not null or undefined
            assertParamExists('updateFormAssignmentSharing', 'updateFormAssignmentSharingRequest', updateFormAssignmentSharingRequest)
            const localVarPath = `/api/v1/clients/{clientId}/form-assignments/{assignmentId}/sharing`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"assignmentId"}}`, encodeURIComponent(String(assignmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateFormAssignmentSharingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FormAssignmentsApi - functional programming interface
 */
export const FormAssignmentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FormAssignmentsApiAxiosParamCreator(configuration)
    return {
        /**
         * Assigns a form to a client for them to fill in via the portal. Only active forms can be assigned. 
         * @summary Assign form to client
         * @param {string} clientId Client UUID
         * @param {AssignFormRequest} assignFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assignFormToClient(clientId: string, assignFormRequest: AssignFormRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFormAssignmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assignFormToClient(clientId, assignFormRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormAssignmentsApi.assignFormToClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Unassigns a form from a client, for example when it was assigned by mistake.  Only pending assignments can be removed. Once the client has completed the form the assignment is the record of that submission and removal is rejected with 409. 
         * @summary Remove a form assignment from a client
         * @param {string} clientId Client UUID
         * @param {string} assignmentId Form assignment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientFormAssignment(clientId: string, assignmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientFormAssignment(clientId, assignmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormAssignmentsApi.deleteClientFormAssignment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all form assignments for a specific client. Can be filtered by status (PENDING, COMPLETED). 
         * @summary List client\'s form assignments
         * @param {string} clientId Client UUID
         * @param {ListClientFormAssignmentsStatusEnum} [status] Filter by assignment status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientFormAssignments(clientId: string, status?: ListClientFormAssignmentsStatusEnum, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFormAssignmentListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientFormAssignments(clientId, status, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormAssignmentsApi.listClientFormAssignments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Controls whether an assigned form is visible in the client portal.  Assigning a form in the dossier does not publish it to the client: portal visibility is opt-in and defaults to hidden. Hiding a form again does not delete answers the client already submitted — it only removes the form from the portal. 
         * @summary Share a form assignment with the client portal, or hide it
         * @param {string} clientId Client UUID
         * @param {string} assignmentId Form assignment UUID
         * @param {UpdateFormAssignmentSharingRequest} updateFormAssignmentSharingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateFormAssignmentSharing(clientId: string, assignmentId: string, updateFormAssignmentSharingRequest: UpdateFormAssignmentSharingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFormAssignmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateFormAssignmentSharing(clientId, assignmentId, updateFormAssignmentSharingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormAssignmentsApi.updateFormAssignmentSharing']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FormAssignmentsApi - factory interface
 */
export const FormAssignmentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FormAssignmentsApiFp(configuration)
    return {
        /**
         * Assigns a form to a client for them to fill in via the portal. Only active forms can be assigned. 
         * @summary Assign form to client
         * @param {string} clientId Client UUID
         * @param {AssignFormRequest} assignFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignFormToClient(clientId: string, assignFormRequest: AssignFormRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientFormAssignmentResponse> {
            return localVarFp.assignFormToClient(clientId, assignFormRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Unassigns a form from a client, for example when it was assigned by mistake.  Only pending assignments can be removed. Once the client has completed the form the assignment is the record of that submission and removal is rejected with 409. 
         * @summary Remove a form assignment from a client
         * @param {string} clientId Client UUID
         * @param {string} assignmentId Form assignment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientFormAssignment(clientId: string, assignmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientFormAssignment(clientId, assignmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all form assignments for a specific client. Can be filtered by status (PENDING, COMPLETED). 
         * @summary List client\'s form assignments
         * @param {string} clientId Client UUID
         * @param {ListClientFormAssignmentsStatusEnum} [status] Filter by assignment status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientFormAssignments(clientId: string, status?: ListClientFormAssignmentsStatusEnum, options?: RawAxiosRequestConfig): AxiosPromise<ClientFormAssignmentListResponse> {
            return localVarFp.listClientFormAssignments(clientId, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Controls whether an assigned form is visible in the client portal.  Assigning a form in the dossier does not publish it to the client: portal visibility is opt-in and defaults to hidden. Hiding a form again does not delete answers the client already submitted — it only removes the form from the portal. 
         * @summary Share a form assignment with the client portal, or hide it
         * @param {string} clientId Client UUID
         * @param {string} assignmentId Form assignment UUID
         * @param {UpdateFormAssignmentSharingRequest} updateFormAssignmentSharingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFormAssignmentSharing(clientId: string, assignmentId: string, updateFormAssignmentSharingRequest: UpdateFormAssignmentSharingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientFormAssignmentResponse> {
            return localVarFp.updateFormAssignmentSharing(clientId, assignmentId, updateFormAssignmentSharingRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FormAssignmentsApi - object-oriented interface
 */
export class FormAssignmentsApi extends BaseAPI {
    /**
     * Assigns a form to a client for them to fill in via the portal. Only active forms can be assigned. 
     * @summary Assign form to client
     * @param {string} clientId Client UUID
     * @param {AssignFormRequest} assignFormRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public assignFormToClient(clientId: string, assignFormRequest: AssignFormRequest, options?: RawAxiosRequestConfig) {
        return FormAssignmentsApiFp(this.configuration).assignFormToClient(clientId, assignFormRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Unassigns a form from a client, for example when it was assigned by mistake.  Only pending assignments can be removed. Once the client has completed the form the assignment is the record of that submission and removal is rejected with 409. 
     * @summary Remove a form assignment from a client
     * @param {string} clientId Client UUID
     * @param {string} assignmentId Form assignment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientFormAssignment(clientId: string, assignmentId: string, options?: RawAxiosRequestConfig) {
        return FormAssignmentsApiFp(this.configuration).deleteClientFormAssignment(clientId, assignmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all form assignments for a specific client. Can be filtered by status (PENDING, COMPLETED). 
     * @summary List client\'s form assignments
     * @param {string} clientId Client UUID
     * @param {ListClientFormAssignmentsStatusEnum} [status] Filter by assignment status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientFormAssignments(clientId: string, status?: ListClientFormAssignmentsStatusEnum, options?: RawAxiosRequestConfig) {
        return FormAssignmentsApiFp(this.configuration).listClientFormAssignments(clientId, status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Controls whether an assigned form is visible in the client portal.  Assigning a form in the dossier does not publish it to the client: portal visibility is opt-in and defaults to hidden. Hiding a form again does not delete answers the client already submitted — it only removes the form from the portal. 
     * @summary Share a form assignment with the client portal, or hide it
     * @param {string} clientId Client UUID
     * @param {string} assignmentId Form assignment UUID
     * @param {UpdateFormAssignmentSharingRequest} updateFormAssignmentSharingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateFormAssignmentSharing(clientId: string, assignmentId: string, updateFormAssignmentSharingRequest: UpdateFormAssignmentSharingRequest, options?: RawAxiosRequestConfig) {
        return FormAssignmentsApiFp(this.configuration).updateFormAssignmentSharing(clientId, assignmentId, updateFormAssignmentSharingRequest, options).then((request) => request(this.axios, this.basePath));
    }
}

export const ListClientFormAssignmentsStatusEnum = {
    Pending: 'PENDING',
    Completed: 'COMPLETED'
} as const;
export type ListClientFormAssignmentsStatusEnum = typeof ListClientFormAssignmentsStatusEnum[keyof typeof ListClientFormAssignmentsStatusEnum];


/**
 * HuisartsApi - axios parameter creator
 */
export const HuisartsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The explicit seed, and the only route from a verwijzer to a cliënt\'s huisarts that a person takes. Two branches, one intent — *this is the cliënt\'s huisarts*:  - nothing recorded (`NONE`): copies the verwijzer of the cliënt\'s active zorgtraject   onto the cliënt as a `HUISARTS` care circle contact. `422` when that verwijzer is   absent or is not a huisarts. - a derived contact standing (`DERIVED_FROM_VERWIJZER`): adopts it. The contact stops   being the verwijzer syncer\'s (#2496) and becomes the cliënt\'s recorded huisarts.   Nothing is copied over it — its fields are already whatever the syncer and the   practice made them.  Nothing here runs on its own, which is the whole point: a derived default that a distracted human confirms is worse than no default at all. 
         * @summary Record who the cliënt\'s huisarts is
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmHuisartsRecipient: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('confirmHuisartsRecipient', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/huisarts/confirm`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers \"who would a letter to the huisarts go to?\" from the cliënt\'s own care circle — the active contacts with role `HUISARTS`. It never reads the zorgtraject verwijzer as a fallback: the verwijzer is who referred *this traject*, which coincides with the current huisarts often and not always, and a letter to last year\'s referrer is a disclosure to the wrong care provider (#4117).  Where a verwijzer *could* be copied onto the cliënt, it is offered as `seedCandidate` and nothing more. Acting on it is `confirmHuisartsRecipient`. 
         * @summary Resolve the cliënt\'s huisarts as a correspondence recipient
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHuisartsRecipient: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getHuisartsRecipient', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/huisarts`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Walks the whole addressing chain in one call so a behandelaar can *see* it before a patient\'s letter leaves (#4121, ADR-041 §4): the cliënt\'s own huisarts (`getHuisartsRecipient`), the AGB code on that contact, and the ZorgMail adresboek behind it.  It resolves **candidates and never a decision.** Every link can be wrong in a way a person notices and the system cannot — a huisarts recorded years ago, a praktijk code carried by eight practitioners, a practice that has left ZorgMail — so which mailbox a letter goes to travels back on `sendLetterViaZorgMail` explicitly. Nothing is sent on a derived default.  There is no fallback. An adresboek that cannot be reached answers `UNAVAILABLE` and stays distinct from a code that is simply not listed: addressing an unverified code anyway is how a letter reaches whoever happens to be behind that number. 
         * @summary Resolve who a ZorgMail letter to the huisarts would reach
         * @param {string} clientId Client UUID
         * @param {string} [agbCode] Look this AGB code up instead of the one on the cliënt\&#39;s huisarts. This is how the recipient is *corrected*: a behandelaar who knows the recorded code is wrong types the right one and sees who it actually reaches, rather than being handed a free-text address field nothing checks. The cliënt\&#39;s own huisarts is still returned beside it, so an override is visibly an override. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgMailRecipient: async (clientId: string, agbCode?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getZorgMailRecipient', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/huisarts/zorgmail-recipient`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (agbCode !== undefined) {
                localVarQueryParameter['agbCode'] = agbCode;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * HuisartsApi - functional programming interface
 */
export const HuisartsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = HuisartsApiAxiosParamCreator(configuration)
    return {
        /**
         * The explicit seed, and the only route from a verwijzer to a cliënt\'s huisarts that a person takes. Two branches, one intent — *this is the cliënt\'s huisarts*:  - nothing recorded (`NONE`): copies the verwijzer of the cliënt\'s active zorgtraject   onto the cliënt as a `HUISARTS` care circle contact. `422` when that verwijzer is   absent or is not a huisarts. - a derived contact standing (`DERIVED_FROM_VERWIJZER`): adopts it. The contact stops   being the verwijzer syncer\'s (#2496) and becomes the cliënt\'s recorded huisarts.   Nothing is copied over it — its fields are already whatever the syncer and the   practice made them.  Nothing here runs on its own, which is the whole point: a derived default that a distracted human confirms is worse than no default at all. 
         * @summary Record who the cliënt\'s huisarts is
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async confirmHuisartsRecipient(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HuisartsRecipientResolution>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.confirmHuisartsRecipient(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HuisartsApi.confirmHuisartsRecipient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers \"who would a letter to the huisarts go to?\" from the cliënt\'s own care circle — the active contacts with role `HUISARTS`. It never reads the zorgtraject verwijzer as a fallback: the verwijzer is who referred *this traject*, which coincides with the current huisarts often and not always, and a letter to last year\'s referrer is a disclosure to the wrong care provider (#4117).  Where a verwijzer *could* be copied onto the cliënt, it is offered as `seedCandidate` and nothing more. Acting on it is `confirmHuisartsRecipient`. 
         * @summary Resolve the cliënt\'s huisarts as a correspondence recipient
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getHuisartsRecipient(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HuisartsRecipientResolution>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getHuisartsRecipient(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HuisartsApi.getHuisartsRecipient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Walks the whole addressing chain in one call so a behandelaar can *see* it before a patient\'s letter leaves (#4121, ADR-041 §4): the cliënt\'s own huisarts (`getHuisartsRecipient`), the AGB code on that contact, and the ZorgMail adresboek behind it.  It resolves **candidates and never a decision.** Every link can be wrong in a way a person notices and the system cannot — a huisarts recorded years ago, a praktijk code carried by eight practitioners, a practice that has left ZorgMail — so which mailbox a letter goes to travels back on `sendLetterViaZorgMail` explicitly. Nothing is sent on a derived default.  There is no fallback. An adresboek that cannot be reached answers `UNAVAILABLE` and stays distinct from a code that is simply not listed: addressing an unverified code anyway is how a letter reaches whoever happens to be behind that number. 
         * @summary Resolve who a ZorgMail letter to the huisarts would reach
         * @param {string} clientId Client UUID
         * @param {string} [agbCode] Look this AGB code up instead of the one on the cliënt\&#39;s huisarts. This is how the recipient is *corrected*: a behandelaar who knows the recorded code is wrong types the right one and sees who it actually reaches, rather than being handed a free-text address field nothing checks. The cliënt\&#39;s own huisarts is still returned beside it, so an override is visibly an override. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgMailRecipient(clientId: string, agbCode?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailRecipientResolution>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgMailRecipient(clientId, agbCode, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HuisartsApi.getZorgMailRecipient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * HuisartsApi - factory interface
 */
export const HuisartsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = HuisartsApiFp(configuration)
    return {
        /**
         * The explicit seed, and the only route from a verwijzer to a cliënt\'s huisarts that a person takes. Two branches, one intent — *this is the cliënt\'s huisarts*:  - nothing recorded (`NONE`): copies the verwijzer of the cliënt\'s active zorgtraject   onto the cliënt as a `HUISARTS` care circle contact. `422` when that verwijzer is   absent or is not a huisarts. - a derived contact standing (`DERIVED_FROM_VERWIJZER`): adopts it. The contact stops   being the verwijzer syncer\'s (#2496) and becomes the cliënt\'s recorded huisarts.   Nothing is copied over it — its fields are already whatever the syncer and the   practice made them.  Nothing here runs on its own, which is the whole point: a derived default that a distracted human confirms is worse than no default at all. 
         * @summary Record who the cliënt\'s huisarts is
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmHuisartsRecipient(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<HuisartsRecipientResolution> {
            return localVarFp.confirmHuisartsRecipient(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Answers \"who would a letter to the huisarts go to?\" from the cliënt\'s own care circle — the active contacts with role `HUISARTS`. It never reads the zorgtraject verwijzer as a fallback: the verwijzer is who referred *this traject*, which coincides with the current huisarts often and not always, and a letter to last year\'s referrer is a disclosure to the wrong care provider (#4117).  Where a verwijzer *could* be copied onto the cliënt, it is offered as `seedCandidate` and nothing more. Acting on it is `confirmHuisartsRecipient`. 
         * @summary Resolve the cliënt\'s huisarts as a correspondence recipient
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getHuisartsRecipient(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<HuisartsRecipientResolution> {
            return localVarFp.getHuisartsRecipient(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Walks the whole addressing chain in one call so a behandelaar can *see* it before a patient\'s letter leaves (#4121, ADR-041 §4): the cliënt\'s own huisarts (`getHuisartsRecipient`), the AGB code on that contact, and the ZorgMail adresboek behind it.  It resolves **candidates and never a decision.** Every link can be wrong in a way a person notices and the system cannot — a huisarts recorded years ago, a praktijk code carried by eight practitioners, a practice that has left ZorgMail — so which mailbox a letter goes to travels back on `sendLetterViaZorgMail` explicitly. Nothing is sent on a derived default.  There is no fallback. An adresboek that cannot be reached answers `UNAVAILABLE` and stays distinct from a code that is simply not listed: addressing an unverified code anyway is how a letter reaches whoever happens to be behind that number. 
         * @summary Resolve who a ZorgMail letter to the huisarts would reach
         * @param {string} clientId Client UUID
         * @param {string} [agbCode] Look this AGB code up instead of the one on the cliënt\&#39;s huisarts. This is how the recipient is *corrected*: a behandelaar who knows the recorded code is wrong types the right one and sees who it actually reaches, rather than being handed a free-text address field nothing checks. The cliënt\&#39;s own huisarts is still returned beside it, so an override is visibly an override. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgMailRecipient(clientId: string, agbCode?: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailRecipientResolution> {
            return localVarFp.getZorgMailRecipient(clientId, agbCode, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * HuisartsApi - object-oriented interface
 */
export class HuisartsApi extends BaseAPI {
    /**
     * The explicit seed, and the only route from a verwijzer to a cliënt\'s huisarts that a person takes. Two branches, one intent — *this is the cliënt\'s huisarts*:  - nothing recorded (`NONE`): copies the verwijzer of the cliënt\'s active zorgtraject   onto the cliënt as a `HUISARTS` care circle contact. `422` when that verwijzer is   absent or is not a huisarts. - a derived contact standing (`DERIVED_FROM_VERWIJZER`): adopts it. The contact stops   being the verwijzer syncer\'s (#2496) and becomes the cliënt\'s recorded huisarts.   Nothing is copied over it — its fields are already whatever the syncer and the   practice made them.  Nothing here runs on its own, which is the whole point: a derived default that a distracted human confirms is worse than no default at all. 
     * @summary Record who the cliënt\'s huisarts is
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public confirmHuisartsRecipient(clientId: string, options?: RawAxiosRequestConfig) {
        return HuisartsApiFp(this.configuration).confirmHuisartsRecipient(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers \"who would a letter to the huisarts go to?\" from the cliënt\'s own care circle — the active contacts with role `HUISARTS`. It never reads the zorgtraject verwijzer as a fallback: the verwijzer is who referred *this traject*, which coincides with the current huisarts often and not always, and a letter to last year\'s referrer is a disclosure to the wrong care provider (#4117).  Where a verwijzer *could* be copied onto the cliënt, it is offered as `seedCandidate` and nothing more. Acting on it is `confirmHuisartsRecipient`. 
     * @summary Resolve the cliënt\'s huisarts as a correspondence recipient
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getHuisartsRecipient(clientId: string, options?: RawAxiosRequestConfig) {
        return HuisartsApiFp(this.configuration).getHuisartsRecipient(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Walks the whole addressing chain in one call so a behandelaar can *see* it before a patient\'s letter leaves (#4121, ADR-041 §4): the cliënt\'s own huisarts (`getHuisartsRecipient`), the AGB code on that contact, and the ZorgMail adresboek behind it.  It resolves **candidates and never a decision.** Every link can be wrong in a way a person notices and the system cannot — a huisarts recorded years ago, a praktijk code carried by eight practitioners, a practice that has left ZorgMail — so which mailbox a letter goes to travels back on `sendLetterViaZorgMail` explicitly. Nothing is sent on a derived default.  There is no fallback. An adresboek that cannot be reached answers `UNAVAILABLE` and stays distinct from a code that is simply not listed: addressing an unverified code anyway is how a letter reaches whoever happens to be behind that number. 
     * @summary Resolve who a ZorgMail letter to the huisarts would reach
     * @param {string} clientId Client UUID
     * @param {string} [agbCode] Look this AGB code up instead of the one on the cliënt\&#39;s huisarts. This is how the recipient is *corrected*: a behandelaar who knows the recorded code is wrong types the right one and sees who it actually reaches, rather than being handed a free-text address field nothing checks. The cliënt\&#39;s own huisarts is still returned beside it, so an override is visibly an override. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgMailRecipient(clientId: string, agbCode?: string, options?: RawAxiosRequestConfig) {
        return HuisartsApiFp(this.configuration).getZorgMailRecipient(clientId, agbCode, options).then((request) => request(this.axios, this.basePath));
    }
}



